/**
 * Description and fix guidance for each issue type.
 * howToFix supports GitHub-flavoured markdown (rendered via react-markdown).
 * wordpressFix is appended when the site is WordPress — CMS-specific guidance.
 */
export interface IssueDescription {
  description: string
  /** Markdown-formatted fix guidance */
  howToFix: string
  /** Additional WordPress-specific fix guidance (markdown). Shown only for WordPress sites. */
  wordpressFix?: string
  /** SEO impact weight 1-5 (5 = critical ranking factor, 1 = minor enhancement) */
  impact: number
}

/** Issue category for grouping in the Issues tab */
export interface IssueCategory {
  label: string
  order: number
}

/** Maps each issue type to a display category */
export const ISSUE_CATEGORIES: Record<string, IssueCategory> = {
  // Title
  missing_title: { label: 'Title', order: 1 },
  title_too_long: { label: 'Title', order: 1 },
  title_too_short: { label: 'Title', order: 1 },
  duplicate_titles: { label: 'Title', order: 1 },
  // Description
  missing_description: { label: 'Description', order: 2 },
  description_too_long: { label: 'Description', order: 2 },
  duplicate_descriptions: { label: 'Description', order: 2 },
  // Headings
  missing_h1: { label: 'Headings', order: 3 },
  multiple_h1: { label: 'Headings', order: 3 },
  h1_too_long: { label: 'Headings', order: 3 },
  duplicate_h1: { label: 'Headings', order: 3 },
  non_sequential_headings: { label: 'Headings', order: 3 },
  // Canonical
  missing_canonical: { label: 'Canonical', order: 4 },
  canonical_mismatch: { label: 'Canonical', order: 4 },
  // Links
  broken_internal_links: { label: 'Links', order: 5 },
  high_outlink_count: { label: 'Links', order: 5 },
  orphan_pages: { label: 'Links', order: 5 },
  nofollow_internal_links: { label: 'Links', order: 5 },
  empty_anchor_text: { label: 'Links', order: 5 },
  broken_external_links: { label: 'Links', order: 5 },
  // Status Codes
  server_errors: { label: 'Status Codes', order: 6 },
  client_errors: { label: 'Status Codes', order: 6 },
  // Images
  images_missing_alt: { label: 'Images', order: 7 },
  images_missing_dimensions: { label: 'Images', order: 7 },
  images_no_lazy_loading: { label: 'Images', order: 7 },
  // Structured Data
  missing_schema: { label: 'Structured Data', order: 8 },
  missing_og: { label: 'Structured Data', order: 8 },
  // Indexing
  noindex_pages: { label: 'Indexing', order: 9 },
  redirect_chains: { label: 'Indexing', order: 9 },
  // Content
  thin_content: { label: 'Content', order: 10 },
  missing_viewport: { label: 'Content', order: 10 },
  mixed_content: { label: 'Content', order: 10 },
  spelling_errors: { label: 'Content', order: 10 },
  spelling_suggestions: { label: 'Content', order: 10 },
  // Security
  missing_hsts: { label: 'Security', order: 11 },
  missing_csp: { label: 'Security', order: 11 },
  missing_x_frame_options: { label: 'Security', order: 11 },
  missing_x_content_type_options: { label: 'Security', order: 11 },
  // Performance
  slow_response: { label: 'Performance', order: 12 },
  // Structure
  high_crawl_depth: { label: 'Structure', order: 13 },
  // Readability
  low_readability: { label: 'Readability', order: 14 },
  // Sitemap
  missing_sitemap: { label: 'Sitemap', order: 15 },
  sitemap_url_not_crawled: { label: 'Sitemap', order: 15 },
  page_not_in_sitemap: { label: 'Sitemap', order: 15 },
  // URL Structure
  url_too_long: { label: 'URL', order: 16 },
  url_has_underscores: { label: 'URL', order: 16 },
  url_has_uppercase: { label: 'URL', order: 16 },
  url_has_parameters: { label: 'URL', order: 16 },
  url_double_slashes: { label: 'URL', order: 16 },
  // Internationalisation
  hreflang_missing_return: { label: 'Internationalisation', order: 17 },
  hreflang_invalid_target: { label: 'Internationalisation', order: 17 },
  og_locale_mismatch: { label: 'Internationalisation', order: 17 },
  // Internal Authority
  low_inrank_pages: { label: 'Links', order: 5 },
  // Soft 404
  soft_404_detected: { label: 'Status Codes', order: 6 },
  // Indexability
  non_indexable_pages: { label: 'Indexing', order: 9 },
  redirect_loop: { label: 'Indexing', order: 9 },
  // Content
  near_duplicate_content: { label: 'Content', order: 10 },
  description_too_short: { label: 'Description', order: 2 },
  large_page_size: { label: 'Performance', order: 12 },
  // Accessibility / Standards
  missing_lang_attribute: { label: 'Indexing', order: 9 },
  missing_favicon: { label: 'Content', order: 10 },
  missing_twitter_card: { label: 'Structured Data', order: 8 },
  links_to_redirects: { label: 'Links', order: 5 },
  too_many_redirects: { label: 'Indexing', order: 9 },
  // 8B: Sitemap cross-checks
  sitemap_url_redirects: { label: 'Sitemap', order: 15 },
  sitemap_url_noindex: { label: 'Sitemap', order: 15 },
  sitemap_url_wrong_canonical: { label: 'Sitemap', order: 15 },
  // 8C: Canonical chain detection
  canonical_chain: { label: 'Canonical', order: 4 },
  canonical_to_noindex: { label: 'Canonical', order: 4 },
  canonical_to_redirect: { label: 'Canonical', order: 4 },
  canonical_to_4xx: { label: 'Canonical', order: 4 },
  // 8D: Redirect status specificity
  temporary_redirect_should_be_permanent: { label: 'Indexing', order: 9 },
  // 8E: Content freshness
  stale_content: { label: 'Content', order: 10 },
  missing_last_modified: { label: 'Content', order: 10 },
  // 8F: Pagination issues
  paginated_noindex: { label: 'Indexing', order: 9 },
  paginated_wrong_canonical: { label: 'Indexing', order: 9 },
  // 9B: Image format
  images_not_modern_format: { label: 'Images', order: 7 },
  // 9C: Breadcrumb schema
  missing_breadcrumb_schema: { label: 'Structured Data', order: 8 },
  // 9D: Accessibility
  missing_form_labels: { label: 'Accessibility', order: 19 },
  generic_link_text: { label: 'Accessibility', order: 19 },
  empty_buttons: { label: 'Accessibility', order: 19 },
  missing_skip_nav: { label: 'Accessibility', order: 19 },
  missing_main_landmark: { label: 'Accessibility', order: 19 },
  // AI Readiness (10E expanded audit)
  ai_fully_blocked: { label: 'AI Readiness', order: 20 },
  ai_partially_blocked: { label: 'AI Readiness', order: 20 },
  ai_blocked_no_llms_txt: { label: 'AI Readiness', order: 20 },
  ai_contradictory_signals: { label: 'AI Readiness', order: 20 },
  ai_missing_llms_txt: { label: 'AI Readiness', order: 20 },
  ai_noai_meta: { label: 'AI Readiness', order: 20 },
  // JS Rendering
  js_rendering_likely: { label: 'Content', order: 7 },
  // Outbound link quality
  spammy_outbound_links: { label: 'Links', order: 5 },
  // Hidden links (cloaking detection)
  hidden_links: { label: 'Links', order: 5 },
  // Phase 4: Crawler & Detection Enhancements
  title_matches_h1: { label: 'Title', order: 1 },
  missing_video_schema: { label: 'Structured Data', order: 8 },
  excessive_iframes: { label: 'Performance', order: 12 },
  excessive_resources: { label: 'Performance', order: 12 },
  missing_responsive_images: { label: 'Images', order: 7 },
  // Core Web Vitals
  slow_lcp: { label: 'Performance', order: 12 },
  high_cls: { label: 'Performance', order: 12 },
  poor_performance: { label: 'Performance', order: 12 },
  // AI Analysis canonical types (normalised from free-form AI output)
  ai_thin_content: { label: 'AI Analysis', order: 21 },
  ai_generic_h1: { label: 'AI Analysis', order: 21 },
  ai_keyword_stuffing: { label: 'AI Analysis', order: 21 },
  ai_missing_schema: { label: 'AI Analysis', order: 21 },
  ai_weak_cta: { label: 'AI Analysis', order: 21 },
  ai_poor_content_structure: { label: 'AI Analysis', order: 21 },
  ai_missing_trust_signals: { label: 'AI Analysis', order: 21 },
  ai_poor_form_ux: { label: 'AI Analysis', order: 21 },
  ai_weak_internal_linking: { label: 'AI Analysis', order: 21 },
  ai_poor_readability: { label: 'AI Analysis', order: 21 },
  ai_missing_portfolio: { label: 'AI Analysis', order: 21 },
  ai_overwhelming_navigation: { label: 'AI Analysis', order: 21 },
  ai_accessibility_issue: { label: 'AI Analysis', order: 21 },
  ai_outdated_content: { label: 'AI Analysis', order: 21 },
  ai_generic_copy: { label: 'AI Analysis', order: 21 },
}

/** AI category mapping from AI issue category to display label */
const AI_CATEGORY_MAP: Record<string, { label: string; order: number }> = {
  seo: { label: 'SEO (AI)', order: 21 },
  content: { label: 'Content (AI)', order: 22 },
  ux: { label: 'UX (AI)', order: 23 },
  accessibility: { label: 'Accessibility (AI)', order: 24 },
  trust: { label: 'Trust & Conversion (AI)', order: 25 },
  technical: { label: 'Technical (AI)', order: 26 },
}

/** Get the category for an issue type, with AI fallback */
export function getIssueCategory(type: string, aiCategory?: string): IssueCategory {
  // Known SQL-computed issue type
  if (ISSUE_CATEGORIES[type]) return ISSUE_CATEGORIES[type]
  // AI-detected issue type — use the AI-provided category
  if (aiCategory && AI_CATEGORY_MAP[aiCategory]) return AI_CATEGORY_MAP[aiCategory]
  // Fallback for unknown AI types
  return { label: 'AI Analysis', order: 27 }
}

/** Severity weight for priority scoring */
const SEVERITY_WEIGHTS: Record<string, number> = { error: 4, warning: 2, info: 1 }

/** Compute a priority score for an issue: severity × impact × affected% */
export function computePriority(type: string, severity: string, count: number, totalPages: number): number {
  const sevWeight = SEVERITY_WEIGHTS[severity] ?? 1
  const impact = issueDescriptions[type]?.impact ?? 2
  const affectedPct = totalPages > 0 ? count / totalPages : 0
  return sevWeight * impact * affectedPct
}

/** Get unique category labels in display order */
export function getCategoryLabels(): string[] {
  const seen = new Map<string, number>()
  for (const cat of Object.values(ISSUE_CATEGORIES)) {
    if (!seen.has(cat.label)) seen.set(cat.label, cat.order)
  }
  return [...seen.entries()].sort((a, b) => a[1] - b[1]).map(([label]) => label)
}

export const issueDescriptions: Record<string, IssueDescription> = {
  // ── Title issues ──────────────────────────────────────────────────────
  missing_title: {
    description:
      'Pages without a title tag. The title tag is one of the most important on-page SEO elements and is displayed as the clickable headline in search results.',
    howToFix: `- **Add a unique \`<title>\` tag** to each page's \`<head>\` section
- Keep titles **30-60 characters** (Google displays ~580px width)
- **Front-load your primary keyword** — put the most important words first
- Use the format: \`Primary Keyword - Description | Brand Name\`
- Avoid boilerplate titles like "Home" or "Untitled"
- Test how your title appears with [Google's title link documentation](https://developers.google.com/search/docs/appearance/title-link)

**Example:**
\`\`\`html
<title>Plantation Shutters Newcastle | The Shutter Guy</title>
\`\`\``,
    wordpressFix: `In **Yoast SEO**: edit the page → scroll to the Yoast panel → click "SEO title" to customise. Or go to **Yoast → Search Appearance → Content Types** to set title templates for all pages.

In **Rank Math**: edit the page → click the Rank Math icon in the toolbar → edit "SEO Title".`,
    impact: 5,
  },
  title_too_long: {
    description:
      'Pages with title tags longer than 60 characters. Google typically displays the first 50-60 characters (or ~580 pixels) of a title tag in search results. Longer titles may be truncated.',
    howToFix: `- **Shorten titles to under 60 characters** — check the SERP preview in this tool
- **Place the most important keywords first** so they're visible even if truncated
- Move the brand name to the end (e.g. \`Page Topic | Brand\`) or remove it if space is tight
- Remove filler words like "Welcome to", "Official", or "Best"
- See [Google's title link best practices](https://developers.google.com/search/docs/appearance/title-link) for how Google may rewrite long titles`,
    wordpressFix: `In **Yoast SEO** or **Rank Math**, edit the page and check the SEO title field. Both plugins show a **character counter** and preview. Remove the \`%%sitename%%\` variable from the template if the brand name pushes the title over 60 characters.`,
    impact: 4,
  },
  title_too_short: {
    description:
      'Pages with title tags shorter than 30 characters. Short titles may not take full advantage of the space available in search results, potentially reducing click-through rates.',
    howToFix: `- **Expand titles to 30-60 characters** to use the full SERP space
- Include the **primary keyword** plus a relevant descriptor
- Add a brand suffix if space allows: \`Primary Topic - Detail | Brand\`
- Short titles often mean missed keyword opportunities — research what users search for

**Example (before/after):**
\`\`\`
Before: <title>About</title>
After:  <title>About Our Team - Newcastle Web Design | Jezweb</title>
\`\`\``,
    wordpressFix: `In **Yoast SEO**: edit the page → expand the Yoast panel → customise the "SEO title" field. By default Yoast uses the page title, which may be short. Add keyword context and your brand name.`,
    impact: 3,
  },
  duplicate_titles: {
    description:
      'Multiple pages share the same title tag. Duplicate titles make it harder for search engines to determine which page is most relevant for a query, and they confuse users in search results. Paginated pages (page 2, 3, etc.) are excluded from this check as they naturally share titles.',
    howToFix: `- **Write a unique title for every page** that reflects its specific content
- For product/service pages, include the **specific product or service name**
- For blog posts, use the **post headline** as the title
- For category pages, include the **category name** and optional parent
- Use templates with dynamic elements for large sites:
  \`\`\`
  {Product Name} - {Category} | {Brand}
  \`\`\`
- See [Google's guidance on unique titles](https://developers.google.com/search/docs/appearance/title-link#page-titles)`,
    wordpressFix: `Check **Yoast SEO → Search Appearance → Content Types** for your title templates. Ensure each template uses dynamic variables like \`%%title%%\`, \`%%category%%\`, or \`%%primary_category%%\` to generate unique titles. If duplicates persist, **manually set SEO titles** on individual pages.`,
    impact: 4,
  },

  // ── Description issues ────────────────────────────────────────────────
  missing_description: {
    description:
      'Pages without a meta description tag. While meta descriptions are not a direct ranking factor, they are often used as the snippet text in search results and influence click-through rates.',
    howToFix: `- **Add a unique \`<meta name="description">\` tag** to each page's \`<head>\`
- Keep descriptions **120-160 characters** (Google displays ~920px width)
- **Include the primary keyword naturally** — Google bolds matching terms in snippets
- Write a **compelling call-to-action** that encourages clicks
- Accurately summarise the page content — misleading descriptions hurt bounce rates

**Example:**
\`\`\`html
<meta name="description" content="Custom plantation shutters in Newcastle. Free measure & quote, Australian-made, installed in 4 weeks. Call 02 4900 1234.">
\`\`\`
- [Google's meta description documentation](https://developers.google.com/search/docs/appearance/snippet)`,
    wordpressFix: `In **Yoast SEO**: edit the page → scroll to the Yoast panel → click "Meta description" to write a custom snippet. Yoast shows a **character counter** and SERP preview.

In **Rank Math**: edit the page → click the Rank Math icon → edit "Description".

For **bulk editing**, use Yoast's "SEO" column in the Posts/Pages list view to quickly spot pages without descriptions.`,
    impact: 4,
  },
  description_too_long: {
    description:
      'Pages with meta descriptions longer than 160 characters. Google typically truncates descriptions at around 155-160 characters (or ~920 pixels) in search results.',
    howToFix: `- **Shorten descriptions to 120-160 characters**
- **Put the most important information first** so it's visible even if truncated
- Remove filler phrases like "Welcome to our website" or "We are a leading provider of"
- Keep the **call-to-action** within the first 155 characters
- Check the SERP preview in this tool to see how your description will appear
- [Google's snippet best practices](https://developers.google.com/search/docs/appearance/snippet)`,
    wordpressFix: `In **Yoast SEO** or **Rank Math**, edit the page and check the meta description field. Both plugins show a **colour-coded character counter** (green = good length, orange/red = too long). Trim from the end while keeping the key message intact.`,
    impact: 3,
  },
  duplicate_descriptions: {
    description:
      'Multiple pages share the same meta description. Duplicate descriptions reduce the uniqueness of each page in search results and may lead Google to generate its own snippet.',
    howToFix: `- **Write a unique meta description for every page** that reflects its specific content
- For similar pages (e.g. product variants), highlight **what makes each page different**
- Use templates with dynamic elements for large sites:
  \`\`\`
  {Product Name} — {key feature}. {Call-to-action}. Shop now at {Brand}.
  \`\`\`
- If you can't write unique descriptions for every page, **leave the tag empty** rather than using duplicates — Google will auto-generate a snippet from the page content
- [Google's snippet documentation](https://developers.google.com/search/docs/appearance/snippet)`,
    wordpressFix: `Check if a **plugin or theme** is auto-generating the same description for multiple pages. In **Yoast SEO → Search Appearance**, review the description templates. If using \`%%excerpt%%\` in templates, ensure each page has a **unique excerpt** set in the WordPress editor.`,
    impact: 3,
  },
  description_too_short: {
    description:
      'Pages with meta descriptions shorter than 70 characters. Short descriptions may not provide enough information to encourage clicks in search results and waste available SERP space.',
    howToFix: `- **Expand meta descriptions to 120-160 characters** to maximise SERP real estate
- Include the **primary keyword** — Google bolds matching terms
- Add a **compelling reason to click**: a benefit, offer, or call-to-action
- Mention specific details: prices, locations, credentials, timeframes

**Example (before/after):**
\`\`\`
Before: "We offer web design services."
After:  "Custom web design for Newcastle businesses. Mobile-friendly, SEO-optimised sites from $2,500. Free consultation — call 02 4900 1234."
\`\`\``,
    wordpressFix: `In **Yoast SEO** or **Rank Math**, edit the page and expand the meta description in the SEO panel. Aim for the **green zone** on the character counter.`,
    impact: 2,
  },

  // ── Heading issues ────────────────────────────────────────────────────
  missing_h1: {
    description:
      'Pages (200 OK) without an H1 heading. The H1 tag helps search engines and users understand the main topic of the page. Every indexable page should have exactly one H1.',
    howToFix: `- **Add a single \`<h1>\` tag** to each page that describes the primary topic
- The H1 should appear **near the top of the content** area
- Include the page's **primary keyword** naturally in the H1
- The H1 and \`<title>\` can be similar but don't need to be identical

**Example:**
\`\`\`html
<h1>Plantation Shutters in Newcastle</h1>
\`\`\``,
    wordpressFix: `WordPress typically uses the **page/post title** as the H1. If it's missing, check your **theme's template files** (\`single.php\`, \`page.php\`) — the H1 might be removed or wrapped in a different tag. Some page builders (Elementor, Divi) let you **hide the title** which removes the H1 — add it back in the builder's heading widget.`,
    impact: 4,
  },
  multiple_h1: {
    description:
      'Pages with more than one H1 tag. While HTML5 technically allows multiple H1s, best practice is to have a single H1 per page to clearly signal the main topic to search engines.',
    howToFix: `- **Keep exactly one \`<h1>\` per page** for the main topic
- **Demote extra H1s to \`<h2>\` or \`<h3>\`** based on their position in the content hierarchy
- Check the page source — extra H1s often come from:
  - Logo/site name wrapped in \`<h1>\`
  - Widget or sidebar headings
  - Page builder sections with default H1 settings`,
    wordpressFix: `Common causes in WordPress:
- **Theme wraps the site name** in \`<h1>\` on every page — edit \`header.php\` to use \`<p>\` or \`<span>\` instead (only the homepage should have the site name as H1)
- **Page builders** (Elementor, Divi) default heading widgets to H1 — change them to H2
- **Widgets in sidebars** using H1 — change to H2 in the widget settings`,
    impact: 3,
  },
  h1_too_long: {
    description:
      'Pages with H1 headings longer than 70 characters. Overly long H1 tags can be less impactful for SEO and may not display well on mobile devices.',
    howToFix: `- **Shorten the H1 to under 70 characters** — concise headings are more impactful
- Move supplementary detail into the **first paragraph** or an \`<h2>\` subheading
- Keep the **primary keyword** in the H1 and expand on it in the body text

**Example (before/after):**
\`\`\`
Before: <h1>The Complete Guide to Choosing the Best Plantation Shutters for Your Newcastle Home in 2026</h1>
After:  <h1>Plantation Shutters Newcastle — Buying Guide</h1>
\`\`\``,
    impact: 2,
  },
  duplicate_h1: {
    description:
      'Multiple pages share the same H1 text. Like duplicate titles, this makes it harder for search engines to differentiate pages and can dilute relevance signals.',
    howToFix: `- **Write a unique H1 for every page** that reflects its specific content
- The H1 should describe **what this particular page is about**, not a generic site-wide heading
- For blog posts, the H1 is typically the **post headline**
- For category/archive pages, include the **category name** in the H1`,
    wordpressFix: `If many pages share the same H1, check whether your **theme template** hard-codes a heading. WordPress should use \`the_title()\` for the H1, which pulls the unique page/post title. If using a **page builder**, ensure each page's heading widget has unique text, not a template default.`,
    impact: 3,
  },
  non_sequential_headings: {
    description:
      'Pages where the heading hierarchy skips levels (e.g., H1 directly to H3, skipping H2). Non-sequential headings can confuse screen readers and reduce the clarity of your content structure.',
    howToFix: `- **Ensure headings follow a logical hierarchy**: \`H1 → H2 → H3 → H4\` without skipping levels
- Each heading level should be a **subsection** of the level above it
- Think of headings like a **document outline**:
  \`\`\`
  H1: Main Topic
    H2: Subtopic A
      H3: Detail of A
    H2: Subtopic B
  \`\`\`
- Don't choose heading levels for visual styling — use CSS instead`,
    wordpressFix: `Common in WordPress when **page builders** or the block editor are used with heading blocks chosen for size rather than hierarchy. In the **block editor**, select each Heading block and check its level in the toolbar. Use the **Document Outline** panel (top-left ≡ icon → Document Overview) to visualise the heading structure.`,
    impact: 2,
  },

  // ── Canonical issues ──────────────────────────────────────────────────
  missing_canonical: {
    description:
      'Pages (200 OK) without a canonical tag. Canonical tags tell search engines which version of a page is the preferred one, helping to prevent duplicate content issues.',
    howToFix: `- **Add a self-referencing canonical tag** to every indexable page:
  \`\`\`html
  <link rel="canonical" href="https://example.com/current-page/">
  \`\`\`
- The canonical URL should be the **full absolute URL** (including https and trailing slash if used)
- For duplicate or similar pages, point the canonical to the **preferred version**
- [Google's canonicalization documentation](https://developers.google.com/search/docs/crawling-indexing/canonicalization)`,
    wordpressFix: `**Yoast SEO** and **Rank Math** automatically add self-referencing canonical tags to all pages. If canonicals are missing:
- Check that **Yoast/Rank Math is active** and not conflicting with another SEO plugin
- Look for themes that output their own \`<link rel="canonical">\` — this can conflict with the plugin's output
- In Yoast, you can override the canonical per page in **Yoast → Advanced → Canonical URL**`,
    impact: 4,
  },
  canonical_mismatch: {
    description:
      'Pages where the canonical URL points to a different page. This tells search engines to consolidate ranking signals to the canonical target, which may be intentional (e.g., pagination) or a misconfiguration.',
    howToFix: `- **Verify each canonical URL is correct** for the page's purpose
- If **intentional** (e.g. paginated pages, URL parameters, print versions) — no action needed
- If **unintentional** — update the canonical to be self-referencing:
  \`\`\`html
  <link rel="canonical" href="https://example.com/this-page/">
  \`\`\`
- Common causes of accidental mismatches:
  - CMS defaulting all pages to the homepage canonical
  - Staging/dev URLs leaking into canonical tags
  - HTTP vs HTTPS or www vs non-www mismatches
- [Google's canonicalization guide](https://developers.google.com/search/docs/crawling-indexing/canonicalization)`,
    wordpressFix: `In **Yoast SEO**: edit the page → Yoast panel → **Advanced tab** → check "Canonical URL". If it's set to a different page, clear it to restore the self-referencing canonical. If many pages have wrong canonicals, check **Yoast → Search Appearance → General** for site-wide canonical settings.`,
    impact: 3,
  },

  // ── Link issues ───────────────────────────────────────────────────────
  broken_internal_links: {
    description:
      'Internal links pointing to pages that return 4xx or 5xx errors. Broken links waste crawl budget, create poor user experience, and can prevent search engines from discovering important content.',
    howToFix: `- **Fix or remove each broken link** — click through to the page detail to see which pages link to the broken URL
- For **404 errors**:
  - Update the link to the correct URL if the page moved
  - Set up a **301 redirect** to the most relevant alternative page
  - Remove the link entirely if no replacement exists
- For **500 errors**: investigate and fix the server-side issue
- **Check for typos** in URLs (missing slashes, wrong file extensions)
- Run this crawl periodically to catch new broken links`,
    wordpressFix: `Install the **[Redirection](https://wordpress.org/plugins/redirection/)** plugin to set up 301 redirects for moved pages. Use **Yoast SEO Premium's redirect manager** if available. Also check **Settings → Permalinks** — simply re-saving permalinks can fix some routing issues.`,
    impact: 5,
  },
  high_outlink_count: {
    description:
      'Pages with more than 100 outgoing internal links. Excessive links can dilute PageRank flow and may indicate navigation or sitemap issues.',
    howToFix: `- **Audit the page** to identify which links are essential
- Common causes of excessive links:
  - **Mega menus** with links to every product/category
  - **Footer link blocks** listing every page on the site
  - **HTML sitemaps** embedded in the page
  - **Tag clouds** or category lists
- Reduce navigation links to the **most important sections** and use dropdowns or JavaScript for deeper links
- Consider whether all links genuinely help user navigation`,
    wordpressFix: `Check your **menu** in **Appearance → Menus** — mega menus with hundreds of items are the most common cause. Reduce the menu to top-level categories and let users drill down. Also check **footer widgets** for long link lists and **tag clouds** in sidebars.`,
    impact: 2,
  },
  orphan_pages: {
    description:
      'Pages (200 OK) with no internal links pointing to them from other pages on the site. Orphan pages may be difficult for search engines to discover and are unlikely to rank well.',
    howToFix: `- **Add internal links to orphan pages** from relevant content elsewhere on the site
- Strategies to fix orphan pages:
  - Add them to **navigation menus** or footer links
  - Link from **related blog posts** or service pages
  - Create a **"Related Services"** or **"You might also like"** section
  - Add **breadcrumb navigation** that includes the page
- If the page is no longer needed, **redirect or remove it**
- Pages found only in the sitemap (not linked from any page) are still considered orphans`,
    wordpressFix: `Use **Yoast SEO's internal linking suggestions** (Premium) or the **Link Whisper** plugin to find contextual linking opportunities. Check that orphan pages appear in at least one **menu**, **category archive**, or **related posts** section.`,
    impact: 3,
  },
  nofollow_internal_links: {
    description:
      'Internal links with rel="nofollow" tell search engines not to follow them. This is almost always a mistake for internal links, as it wastes crawl budget and prevents PageRank from flowing to your own pages.',
    howToFix: `- **Remove \`rel="nofollow"\` from internal links** — this attribute should only be used on:
  - Links to **login/register** pages
  - Links to **internal search results**
  - Paid or sponsored internal links (rare)
- Check your HTML templates and CMS settings for nofollow being applied globally
- Common causes:
  - Comment sections auto-adding nofollow to all links
  - Plugins/themes adding nofollow to navigation links
  - Manual addition during content editing`,
    wordpressFix: `Check if a **plugin** is adding nofollow to internal links. Common culprits:
- **External Links** plugins that accidentally nofollow internal links too
- **Theme settings** that add nofollow to sidebar/footer links
- The **WordPress block editor** adds \`rel="nofollow"\` when you check "nofollow" in the link options — uncheck it for internal links`,
    impact: 2,
  },
  empty_anchor_text: {
    description:
      'Links with no anchor text (empty or whitespace-only). These provide no context to search engines about the destination page and are poor for accessibility.',
    howToFix: `- **Add descriptive anchor text** that explains where the link goes
- For **image-only links**, add meaningful \`alt\` text to the image:
  \`\`\`html
  <a href="/services"><img src="icon.png" alt="Our services"></a>
  \`\`\`
- For **icon links**, add an \`aria-label\`:
  \`\`\`html
  <a href="/search" aria-label="Search the site"><svg>...</svg></a>
  \`\`\`
- Avoid generic text like "click here" — use descriptive, keyword-relevant phrases`,
    wordpressFix: `Common in WordPress when **image links** in the content don't have alt text. Edit the image in the block editor and add alt text in the **Image settings** panel. For **icon links** in menus or widgets, check the theme's widget settings for label/aria-label options.`,
    impact: 2,
  },
  broken_external_links: {
    description:
      'Outbound links pointing to external pages that return errors (4xx, 5xx, or unreachable). Broken external links degrade user experience and may signal poor site maintenance to search engines.',
    howToFix: `- **Review each broken external link** in the External Links tab:
  - **Remove** the link if the resource no longer exists
  - **Update** the URL if the page has moved (check for a new URL via web search)
  - **Replace** with an alternative resource
- External links break naturally over time as third-party sites change — **audit periodically**
- For links you want to keep but can't control (e.g. partner sites), consider linking to an archived version via [web.archive.org](https://web.archive.org)`,
    wordpressFix: `Use the **[Broken Link Checker](https://wordpress.org/plugins/broken-link-checker/)** plugin to get notified when external links break. It can also help you bulk-edit or unlink broken URLs directly from the WordPress dashboard.`,
    impact: 3,
  },
  links_to_redirects: {
    description:
      'Internal links pointing to pages that return 301/302 redirects instead of 200 OK. While the user eventually reaches the correct page, linking to redirects adds unnecessary latency and wastes crawl budget.',
    howToFix: `- **Update internal links** to point directly to the **final destination URL**
- Run a find-and-replace across your site content and templates to update old URLs
- Common causes:
  - URL changes after a site migration or permalink update
  - HTTP links not updated to HTTPS
  - www/non-www links not standardised
  - Trailing slash inconsistencies
- Fixing these improves page load speed and preserves full link equity`,
    wordpressFix: `Use the **[Better Search Replace](https://wordpress.org/plugins/better-search-replace/)** plugin to bulk-update old URLs in post content. For template/menu links, update them in **Appearance → Menus** and **Appearance → Widgets**. If you recently changed permalinks, update all internal links to match the new structure.`,
    impact: 3,
  },
  spammy_outbound_links: {
    description:
      'This site links to domains with high spam scores (60+) according to DataForSEO. Linking to spammy or low-quality domains can negatively impact your site\'s trustworthiness in the eyes of search engines.',
    howToFix: `- **Review the External Links tab** to identify high spam-score domains
- For each spammy link, decide:
  - **Remove** the link if it's not essential
  - **Add \`rel="nofollow"\`** if the link is necessary but you don't want to pass authority
  - **Add \`rel="nofollow ugc"\`** for user-generated content (comments, forum posts)
- Common sources of spammy links:
  - Comment spam that slipped through moderation
  - Old partner/sponsor links to sites that have since been compromised
  - Hacked injected links (check page source for hidden links)`,
    wordpressFix: `- Check **comment moderation settings** in **Settings → Discussion** — enable link moderation
- Install **Akismet** or **Anti-Spam Bee** to catch comment spam
- Review **widget areas** for injected links (sidebar, footer)
- If you suspect the site was **hacked**, scan with **Wordfence** or **Sucuri** and check for hidden \`<a>\` tags in theme files`,
    impact: 3,
  },

  // ── Status code issues ────────────────────────────────────────────────
  server_errors: {
    description:
      'Pages returning 5xx server errors (500 Internal Server Error, 502 Bad Gateway, 503 Service Unavailable, etc.). These indicate server-side problems that prevent the page from being served.',
    howToFix: `- **Investigate server logs** to identify the root cause
- Common causes by error code:
  - **500 Internal Server Error**: application code error, misconfigured \`.htaccess\`, PHP fatal error
  - **502 Bad Gateway**: upstream server (PHP-FPM, Node.js) crashed or timed out
  - **503 Service Unavailable**: server overloaded, maintenance mode, rate limiting
- **Check server resource limits** (memory, CPU, database connections)
- If intermittent, check for **traffic spikes** or **cron jobs** that overload the server
- Set up **uptime monitoring** to catch 5xx errors early`,
    wordpressFix: `Common WordPress 500 error causes:
- **Plugin conflict** — deactivate all plugins, reactivate one by one to find the culprit
- **Corrupted \`.htaccess\`** — rename \`.htaccess\` and re-save permalinks in Settings → Permalinks
- **PHP memory limit** — add \`define('WP_MEMORY_LIMIT', '256M');\` to \`wp-config.php\`
- **Theme error** — switch to a default theme (Twenty Twenty-Four) temporarily to test
- Check \`wp-content/debug.log\` if \`WP_DEBUG_LOG\` is enabled`,
    impact: 5,
  },
  client_errors: {
    description:
      'Pages returning 4xx client errors (400 Bad Request, 403 Forbidden, 404 Not Found, 410 Gone, 429 Too Many Requests). The most common is 404, indicating the page does not exist.',
    howToFix: `- **For 404 Not Found**:
  - Set up a **301 redirect** to the most relevant replacement page
  - Update or remove internal links pointing to the deleted page
  - If the page was never important, ensure a **custom 404 page** helps users navigate
- **For 403 Forbidden**: check file permissions and \`.htaccess\` access restrictions
- **For 410 Gone**: confirm the page was intentionally removed (410 tells Google to deindex faster than 404)
- **For 429 Too Many Requests**: the server is rate-limiting the crawler — increase rate limits or slow the crawl
- [Check Google Search Console](https://search.google.com/search-console) for crawl errors Google has found`,
    wordpressFix: `- Install the **[Redirection](https://wordpress.org/plugins/redirection/)** plugin and set up 301 redirects for important deleted pages
- Check **Settings → Permalinks** — re-saving can fix 404s caused by missing rewrite rules
- For 403 errors, check \`wp-content\` file permissions (directories: 755, files: 644)
- Create a **custom 404 page** with helpful navigation using a page builder or a plugin like **Smart 404**`,
    impact: 4,
  },
  soft_404_detected: {
    description:
      'Pages returning HTTP 200 but appearing to be error pages (title or H1 contains "not found", "404", etc.). Soft 404s confuse search engines and waste crawl budget.',
    howToFix: `- **Return a proper 404 or 410 status code** for pages that don't exist
- If the page has legitimate content but a misleading title, **update the title/H1** to remove "not found" or "404" references
- Common causes of soft 404s:
  - Custom 404 templates that return \`200 OK\` instead of \`404\`
  - Search results pages with zero results
  - Empty category/tag archives
- Google Search Console flags soft 404s — [check your coverage report](https://search.google.com/search-console)`,
    wordpressFix: `WordPress should return 404 status codes automatically for missing pages. If you're getting soft 404s:
- Check if your **theme's 404.php** template is correct (it should exist and WordPress handles the status code)
- A **caching plugin** might be caching 404 pages with a 200 status — clear the cache and check caching settings
- **Empty archive pages** (categories with no posts) can trigger this — either add content or noindex empty archives in **Yoast SEO → Search Appearance → Taxonomies**`,
    impact: 4,
  },

  // ── Image issues ──────────────────────────────────────────────────────
  images_missing_alt: {
    description:
      'Pages containing images without alt text attributes. Alt text helps search engines understand image content, improves accessibility for screen reader users, and is displayed when images fail to load.',
    howToFix: `- **Add descriptive \`alt\` text** to all meaningful images:
  \`\`\`html
  <img src="team-photo.jpg" alt="Jezweb team at the Newcastle office">
  \`\`\`
- **Decorative images** (backgrounds, spacers) should use an empty alt: \`alt=""\`
- Keep alt text **concise but descriptive** (under 125 characters)
- **Don't keyword-stuff** — describe what the image actually shows
- Include the alt text naturally — imagine describing the image to someone on the phone
- [Google's image SEO best practices](https://developers.google.com/search/docs/appearance/google-images)`,
    wordpressFix: `When uploading images in WordPress, fill in the **"Alt Text"** field in the **Media Library** or the image block settings panel. For existing images, go to **Media → Library**, click each image, and add alt text. The **Yoast SEO** readability analysis also flags missing alt text on images.`,
    impact: 3,
  },
  images_missing_dimensions: {
    description:
      'Pages containing images without explicit width and height attributes. Missing dimensions cause layout shifts (CLS) as the browser cannot reserve space before the image loads, negatively impacting Core Web Vitals.',
    howToFix: `- **Add \`width\` and \`height\` attributes** to all \`<img>\` tags:
  \`\`\`html
  <img src="photo.jpg" alt="Description" width="800" height="600">
  \`\`\`
- Set dimensions to the image's **natural (intrinsic) size** — CSS will handle responsive display
- Use **CSS \`aspect-ratio\`** for responsive containers:
  \`\`\`css
  img { aspect-ratio: 4/3; width: 100%; height: auto; }
  \`\`\`
- This prevents **Cumulative Layout Shift (CLS)** which is a [Core Web Vital](https://web.dev/vitals/)`,
    wordpressFix: `WordPress **automatically adds width and height** to images inserted via the editor. If dimensions are missing:
- The images may have been **inserted as raw HTML** — edit the block and add dimensions
- A **lazy loading plugin** might be stripping dimensions — check plugin settings
- Re-upload images through the **Media Library** to let WordPress generate proper attributes`,
    impact: 2,
  },
  images_no_lazy_loading: {
    description:
      'Pages containing images without the loading="lazy" attribute. Lazy loading defers off-screen images until the user scrolls near them, reducing initial page load time and bandwidth usage.',
    howToFix: `- **Add \`loading="lazy"\`** to images below the fold:
  \`\`\`html
  <img src="photo.jpg" alt="Description" loading="lazy" width="800" height="600">
  \`\`\`
- The **first visible image** (hero/above-fold) should use \`loading="eager"\` or omit the attribute
- Native browser lazy loading requires **no JavaScript** — it's built into all modern browsers
- Don't lazy-load the **LCP (Largest Contentful Paint) element** — this hurts performance
- [Web.dev lazy loading guide](https://web.dev/articles/browser-level-image-lazy-loading)`,
    wordpressFix: `WordPress **automatically adds \`loading="lazy"\`** to images since version 5.5. If it's missing:
- Ensure WordPress is **up to date** (5.5+)
- A plugin might be **removing the attribute** — check caching and optimisation plugins
- For the **hero image**, add \`loading="eager"\` or use the \`wp_img_tag_add_loading_optimization_attrs\` filter to exclude above-fold images`,
    impact: 1,
  },
  images_not_modern_format: {
    description:
      'Pages containing images in legacy formats (JPEG, PNG, GIF, BMP) instead of modern formats like WebP or AVIF. Modern image formats provide significantly better compression (25-50% smaller file sizes) with equivalent visual quality, leading to faster page loads and better Core Web Vitals scores.',
    howToFix: `- **Convert images to WebP** (broad browser support) or **AVIF** (better compression, newer browsers)
- Use the \`<picture>\` element for format fallbacks:
  \`\`\`html
  <picture>
    <source srcset="photo.avif" type="image/avif">
    <source srcset="photo.webp" type="image/webp">
    <img src="photo.jpg" alt="Description" width="800" height="600">
  </picture>
  \`\`\`
- Use a **CDN with auto-conversion** (Cloudflare, Imgix, Cloudinary) for zero-effort conversion
- Test visual quality after conversion — some images (text, sharp edges) may need higher quality settings
- [PageSpeed Insights](https://pagespeed.web.dev/) flags legacy format images as an opportunity`,
    wordpressFix: `Install an **image optimisation plugin** that auto-converts uploads to WebP:
- **[ShortPixel](https://wordpress.org/plugins/shortpixel-image-optimiser/)** — auto-converts on upload + bulk optimises existing images
- **[Imagify](https://wordpress.org/plugins/imagify/)** — from the WP Rocket team, simple WebP conversion
- **[EWWW Image Optimizer](https://wordpress.org/plugins/ewww-image-optimizer/)** — free WebP conversion
- If using **Cloudflare**, enable **Polish** (Pro plan) for automatic WebP/AVIF conversion at the CDN level`,
    impact: 2,
  },

  // ── Structured data issues ────────────────────────────────────────────
  missing_schema: {
    description:
      'Pages (200 OK) without structured data (JSON-LD schema markup). Structured data helps search engines understand your content and can enable rich results (rich snippets) in search results.',
    howToFix: `- **Add JSON-LD structured data** appropriate to each page type:
  - **Homepage**: \`Organization\` or \`LocalBusiness\`
  - **About page**: \`Organization\` with team/founder info
  - **Blog posts**: \`Article\` or \`BlogPosting\`
  - **Service pages**: \`Service\` or \`Product\`
  - **Contact page**: \`LocalBusiness\` with address, phone, hours
  - **FAQ sections**: \`FAQPage\`
- **Validate** with [Google's Rich Results Test](https://search.google.com/test/rich-results)
- Place JSON-LD in a \`<script type="application/ld+json">\` tag in the \`<head>\`
- [Google's structured data documentation](https://developers.google.com/search/docs/appearance/structured-data)`,
    wordpressFix: `- **Yoast SEO** automatically adds \`Organization\`, \`WebSite\`, \`Article\`, and \`BreadcrumbList\` schema
- **Rank Math** adds similar schema and has a **Schema Generator** for custom types (FAQ, HowTo, Product)
- For **LocalBusiness** schema, configure it in **Yoast → Search Appearance → Organization** or install the **[Schema Pro](https://wordpress.org/plugins/wp-schema-pro/)** plugin
- For **FAQ schema**, use Yoast's FAQ block in the block editor`,
    impact: 2,
  },
  missing_og: {
    description:
      'Pages (200 OK) without Open Graph tags. OG tags control how your content appears when shared on social media platforms like Facebook, LinkedIn, and Twitter.',
    howToFix: `- **Add Open Graph meta tags** to the \`<head>\`:
  \`\`\`html
  <meta property="og:title" content="Page Title">
  <meta property="og:description" content="Compelling description for social sharing">
  <meta property="og:image" content="https://example.com/image-1200x630.jpg">
  <meta property="og:url" content="https://example.com/page/">
  <meta property="og:type" content="website">
  \`\`\`
- The \`og:image\` should be **at least 1200x630 pixels** for optimal display
- Test with [Facebook's Sharing Debugger](https://developers.facebook.com/tools/debug/) and [LinkedIn's Post Inspector](https://www.linkedin.com/post-inspector/)`,
    wordpressFix: `**Yoast SEO** and **Rank Math** automatically generate OG tags from the page title, meta description, and featured image. If OG tags are missing:
- Ensure a **Featured Image** is set on each page/post
- In **Yoast → Social**, configure default OG images and enable Open Graph output
- Override per-page OG details in the **Yoast Social tab** when editing a page`,
    impact: 2,
  },
  missing_twitter_card: {
    description:
      'Pages without Twitter/X Card meta tags (no <meta name="twitter:card"> found). Twitter Cards control how content appears when shared on Twitter/X. Without them, shared links display as plain URLs without preview images or descriptions.',
    howToFix: `- **Add Twitter Card meta tags** to the \`<head>\`:
  \`\`\`html
  <meta name="twitter:card" content="summary_large_image">
  <meta name="twitter:title" content="Page Title">
  <meta name="twitter:description" content="Compelling description">
  <meta name="twitter:image" content="https://example.com/image-1200x630.jpg">
  \`\`\`
- Use \`summary_large_image\` for pages with strong imagery, \`summary\` for text-focused pages
- Twitter falls back to OG tags if Twitter-specific tags are missing, but explicit tags give more control
- Test with [Twitter's Card Validator](https://cards-dev.twitter.com/validator)`,
    wordpressFix: `**Yoast SEO** automatically outputs Twitter Card tags. Check:
- **Yoast → Social → Twitter** tab — ensure Twitter card output is enabled
- Set a **default card type** (summary_large_image recommended)
- Override per-page in the **Yoast Social tab** when editing a page
- **Rank Math** has equivalent settings under **Rank Math → Titles & Meta → Social**`,
    impact: 1,
  },
  missing_breadcrumb_schema: {
    description:
      'Pages 3 or more clicks deep in the site hierarchy that lack BreadcrumbList structured data (JSON-LD). Breadcrumb schema helps search engines understand the site structure and can display breadcrumb trails in search results, improving click-through rates and user navigation context.',
    howToFix: `- **Add \`BreadcrumbList\` JSON-LD** to pages deep in the site hierarchy:
  \`\`\`json
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://example.com/" },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://example.com/services/" },
      { "@type": "ListItem", "position": 3, "name": "Web Design" }
    ]
  }
  \`\`\`
- The **last item** should be the current page (no \`item\` URL needed)
- [Google's breadcrumb structured data guide](https://developers.google.com/search/docs/appearance/structured-data/breadcrumb)
- Validate with the [Rich Results Test](https://search.google.com/test/rich-results)`,
    wordpressFix: `- **Yoast SEO** has built-in breadcrumb support: go to **Yoast → Search Appearance → Breadcrumbs** and enable it. Then add the breadcrumb shortcode or function to your theme: \`<?php yoast_breadcrumb(); ?>\`
- **Rank Math** also supports breadcrumbs with JSON-LD output — enable in **Rank Math → General → Breadcrumbs**
- Many themes (Astra, GeneratePress, Kadence) have **built-in breadcrumb** support that outputs schema automatically`,
    impact: 2,
  },

  // ── Indexing issues ───────────────────────────────────────────────────
  noindex_pages: {
    description:
      'Pages with a meta robots noindex directive. These pages will not appear in search engine results. This is informational — noindex may be intentional for pages like login, thank you, or admin pages.',
    howToFix: `- **Review each noindexed page** and confirm the directive is intentional
- Pages that **should be noindexed**: login, registration, thank you, admin, staging, search results, thin utility pages
- Pages that **should NOT be noindexed**: service pages, blog posts, product pages, important landing pages
- If noindex is unintentional, **remove the directive**:
  \`\`\`html
  <!-- Remove this: -->
  <meta name="robots" content="noindex">
  \`\`\`
- Also check for \`X-Robots-Tag: noindex\` in HTTP headers
- [Google's robots meta tag documentation](https://developers.google.com/search/docs/crawling-indexing/robots-meta-tag)`,
    wordpressFix: `- Check **Settings → Reading** — ensure "Discourage search engines from indexing this site" is **unchecked**
- In **Yoast SEO**: edit the page → Advanced tab → set "Allow search engines to show this page" to **Yes**
- Check **Yoast → Search Appearance → Content Types** for global noindex settings per post type
- Some **maintenance mode plugins** add noindex site-wide — deactivate them after launching`,
    impact: 2,
  },
  redirect_chains: {
    description:
      'Pages that redirect through one or more intermediate URLs before reaching the final destination. Redirect chains waste crawl budget, slow page loading, and may lose PageRank at each hop.',
    howToFix: `- **Replace chains with direct redirects** — instead of A → B → C, make it A → C
- **Update internal links** to point to the final destination URL, not the redirect source
- Common causes:
  - HTTP → HTTPS → www → non-www chain (fix at server level)
  - Old redirect + new redirect stacking over time
  - CMS adding trailing slashes on top of existing redirects
- Use the redirect chain detail view in this tool to see each hop`,
    wordpressFix: `- Check the **[Redirection](https://wordpress.org/plugins/redirection/)** plugin for stacked redirects — old redirect A→B plus new B→C creates a chain. Update A to point directly to C.
- Check **Settings → Permalinks** and your **.htaccess** file for conflicting redirect rules
- **Yoast SEO Premium** has a redirect manager that can detect and fix chains`,
    impact: 3,
  },
  redirect_loop: {
    description:
      'Pages that redirect back to themselves or create circular redirect chains. Redirect loops prevent the page from loading and cause search engines to abandon crawling the URL.',
    howToFix: `- **Audit redirect rules** in your server config (\`.htaccess\`, nginx, Cloudflare Page Rules) to remove circular references
- Common causes:
  - \`.htaccess\` rules that conflict (e.g. www redirect + non-www redirect both active)
  - CMS redirect plugins with conflicting rules
  - CDN/proxy redirects conflicting with origin server redirects
- Ensure each redirect points to a **final destination** that returns \`200 OK\`
- Test each hop individually: \`curl -I -L <url>\` to trace the chain`,
    wordpressFix: `Common WordPress redirect loop causes:
- **WordPress Address and Site Address mismatch** in **Settings → General** (both should match)
- **Conflicting plugins** — deactivate redirect/caching plugins one at a time
- **Cloudflare + WordPress** SSL conflict — set Cloudflare SSL to "Full (Strict)" instead of "Flexible"
- **Corrupted \`.htaccess\`** — rename it and re-save **Settings → Permalinks**`,
    impact: 5,
  },
  non_indexable_pages: {
    description:
      'Pages that return 200 OK but are excluded from search engine indexes. Common reasons: a `noindex` meta robots directive, or a canonical tag pointing to a different URL. Pages with non-self canonicals tell Google "index that URL instead of this one" — the page itself may work fine, but Google will prefer the canonical version.',
    howToFix: `- **Review each page** to determine if the exclusion is intentional
- If the page **should be indexed**:
  - Remove the \`noindex\` directive from \`<meta name="robots">\`
  - Fix the canonical tag to be **self-referencing**
- If the page **should not be indexed**, consider:
  - Does it still need to exist? (redirect or remove if unnecessary)
  - Is it serving a purpose for users? (keep it, noindex is fine)
- [Google's indexing documentation](https://developers.google.com/search/docs/crawling-indexing/robots-meta-tag)`,
    wordpressFix: `In **Yoast SEO**: edit the page → Advanced tab → check both "Allow search engines to show this page" and the "Canonical URL" field. If the canonical points elsewhere, clear it to restore self-referencing. In **Rank Math**, check the same settings under the Advanced tab.`,
    impact: 3,
  },
  temporary_redirect_should_be_permanent: {
    description:
      'Pages using temporary redirects (302 Found or 307 Temporary Redirect) instead of permanent redirects (301 Moved Permanently or 308 Permanent Redirect). Temporary redirects tell search engines the original URL may come back, so they keep the old URL in the index and don\'t pass full link equity to the target.',
    howToFix: `- **Change 302/307 redirects to 301** if the redirect is intended to be permanent
- 301 redirects tell search engines to:
  - **Transfer link equity** (PageRank) to the new URL
  - **Update their index** to the new URL
  - **Stop crawling** the old URL
- Keep 302/307 **only** for genuinely temporary moves:
  - A/B testing
  - Maintenance pages
  - Geo-targeted temporary redirects
- Common cause: many CMS redirect features default to 302 — check and change to 301`,
    wordpressFix: `In the **[Redirection](https://wordpress.org/plugins/redirection/)** plugin, edit each redirect and change the type from "302 Found" to **"301 Moved Permanently"**. In **Yoast SEO Premium's redirect manager**, the default is 301 — check for any manually set to 302.`,
    impact: 3,
  },
  too_many_redirects: {
    description:
      'Pages with redirect chains longer than 3 hops. Each redirect adds latency, wastes crawl budget, and may lose PageRank at each hop. Very long chains risk being abandoned by search engine crawlers.',
    howToFix: `- **Collapse redirect chains** to a single hop: point the original URL directly to the final destination
- Example: \`A → B → C → D\` should become \`A → D\`
- **Audit all redirect rules** in:
  - Server config (\`.htaccess\`, nginx conf)
  - CDN/proxy settings (Cloudflare Page Rules)
  - CMS redirect plugins
- Long chains often accumulate over **multiple site migrations** — audit and clean up periodically`,
    wordpressFix: `Open the **[Redirection](https://wordpress.org/plugins/redirection/)** plugin and look for chains where redirect A→B and B→C both exist. Update A to point directly to C. The Redirection plugin's **"Redirect Chains"** feature (if available) can detect these automatically.`,
    impact: 4,
  },
  paginated_noindex: {
    description:
      'Paginated pages (those with rel=prev or rel=next links) that have a noindex directive. Noindexing paginated pages can prevent search engines from discovering content linked only from those pages. While Google says rel=prev/next is a hint, noindexing paginated pages removes them from the index entirely.',
    howToFix: `- **Remove the noindex directive** from paginated pages
- If you want to consolidate ranking signals:
  - Use a **self-referencing canonical** on each paginated page (page 2 canonicals to page 2)
  - Or implement a **"View All" page** and canonical all paginated pages to it
- Don't combine noindex with pagination — it hides content from search engines
- [Google's pagination guidance](https://developers.google.com/search/docs/specialty/ecommerce/pagination-and-incremental-page-loading)`,
    wordpressFix: `Check **Yoast SEO → Search Appearance → Archives** — if "Subpages of archives" is set to noindex, change it to "Index". Some themes and plugins add noindex to paginated pages independently — check your theme's \`archive.php\` or \`index.php\` for hardcoded noindex meta tags.`,
    impact: 3,
  },
  paginated_wrong_canonical: {
    description:
      'Paginated pages (those with rel=prev or rel=next links) where the canonical tag points to a different URL (typically page 1). This tells search engines to ignore the paginated page in favour of page 1, which can prevent content on later pages from being indexed.',
    howToFix: `- **Set self-referencing canonicals** on each paginated page:
  - Page 2 should canonical to page 2, not page 1
  - Page 3 should canonical to page 3, and so on
- Alternatively, implement a **"View All" page** and canonical all paginated pages to it
- The key rule: each page's canonical must point to a URL that **contains its content**
- [Google's canonicalization guidance](https://developers.google.com/search/docs/crawling-indexing/canonicalization)`,
    wordpressFix: `**Yoast SEO** should handle paginated canonicals correctly by default. If paginated pages are canonicalising to page 1:
- Check for a **custom canonical** set on the archive/category page (Yoast Advanced tab) — clear it
- Check for a **plugin or theme** that overrides canonicals on paginated pages
- Ensure you're running the **latest version** of your SEO plugin`,
    impact: 3,
  },

  // ── Content issues ────────────────────────────────────────────────────
  thin_content: {
    description:
      'Pages (200 OK) with fewer than 200 words of content. Thin content pages may not provide enough value to rank well and can be flagged by Google\'s thin content quality guidelines.',
    howToFix: `- **Expand content** to provide comprehensive coverage of the topic (aim for 300+ words minimum)
- Options for handling thin pages:
  - **Add more content**: expand with useful information, FAQs, examples
  - **Consolidate**: merge thin pages into a single, comprehensive resource and redirect the old URLs
  - **Noindex**: if the page doesn't warrant more content (e.g. contact, login), add \`noindex\`
  - **Remove**: delete truly unnecessary pages and redirect to a relevant alternative
- Focus on **quality over quantity** — 300 words of useful content beats 1000 words of fluff`,
    wordpressFix: `Common thin content in WordPress:
- **Tag/category archives** with few posts — either add descriptive text to the archive (Yoast has a "Category description" field) or noindex them in **Yoast → Search Appearance → Taxonomies**
- **Author archives** — noindex in Yoast if authors have few posts
- **Placeholder pages** — drafts or "Coming soon" pages that were accidentally published`,
    impact: 3,
  },
  missing_viewport: {
    description:
      'Pages (200 OK) without a viewport meta tag. The viewport tag is essential for responsive design and mobile-friendly pages, which is a Google ranking factor.',
    howToFix: `- **Add the viewport meta tag** to every page's \`<head>\`:
  \`\`\`html
  <meta name="viewport" content="width=device-width, initial-scale=1">
  \`\`\`
- This is **required for mobile-friendly rendering** — without it, mobile browsers render the page at desktop width and zoom out
- [Google's mobile-first indexing documentation](https://developers.google.com/search/docs/crawling-indexing/mobile/mobile-sites-mobile-first-indexing)`,
    wordpressFix: `The viewport tag should be in your **theme's \`header.php\`** or \`functions.php\`. If it's missing, your theme is outdated. Options:
- **Update your theme** to the latest version
- Manually add the viewport tag to \`header.php\` inside \`<head>\`
- Switch to a **modern theme** (Astra, GeneratePress, Kadence) that includes it by default`,
    impact: 1,
  },
  mixed_content: {
    description:
      'HTTPS pages loading resources (images, scripts, stylesheets) over HTTP. Mixed content triggers browser security warnings and can prevent resources from loading in modern browsers.',
    howToFix: `- **Update all resource URLs to HTTPS** — search for \`http://\` references in:
  - HTML templates and content
  - CSS files (\`url()\` references)
  - JavaScript files (API calls, resource URLs)
- Use **protocol-relative URLs** (\`//example.com/image.jpg\`) or **absolute HTTPS URLs**
- Check for hardcoded HTTP URLs in the **database content** (blog posts, pages)
- If using a CDN, ensure it's configured for **HTTPS delivery**`,
    wordpressFix: `- Update **Settings → General** — both WordPress Address and Site Address should use \`https://\`
- Use **[Better Search Replace](https://wordpress.org/plugins/better-search-replace/)** to replace \`http://yourdomain.com\` with \`https://yourdomain.com\` across the database
- Check **theme and plugin settings** for hardcoded HTTP URLs (custom CSS, widget content, theme options)
- If behind Cloudflare, enable **Automatic HTTPS Rewrites** in the Cloudflare dashboard`,
    impact: 3,
  },
  spelling_errors: {
    description:
      'Pages with definite spelling mistakes, typos, or grammar errors detected by AI analysis (Australian English). These are genuine errors that should be fixed — misspelled words, broken words, missing apostrophes, and incorrect word usage.',
    howToFix: `- **Review each flagged word** — these are high-confidence errors identified by AI with context awareness
- **Fix misspellings** directly in your CMS (e.g. "RECCOMMEND" → "RECOMMEND", "Bipass" → "Bi-pass")
- **Wrong word errors** like "compliment" vs "complement" are common and worth fixing — they affect credibility
- Focus on errors that appear **on key landing pages** first (home, services, contact)`,
    wordpressFix: `- Edit the page in WordPress and use your browser's built-in spellcheck (set to **English (Australia)**)
- For errors in **theme files** (button text, footer, placeholder text), edit the theme or use a child theme
- For bulk fixes, use **[Better Search Replace](https://wordpress.org/plugins/better-search-replace/)** to correct repeated misspellings across all content`,
    impact: 2,
  },
  spelling_suggestions: {
    description:
      'Pages with writing style improvements suggested by AI analysis. These are not errors — they are opportunities to improve clarity, readability, or phrasing. Review and apply at your discretion.',
    howToFix: `- **These are optional** — your current text is not wrong, but could be improved
- Common suggestions include:
  - Wordy phrases that could be simplified ("prior to" → "before")
  - Awkward phrasing that could flow better
  - Inconsistent formatting or capitalisation
- **Prioritise suggestions on high-traffic pages** where polish matters most
- Feel free to dismiss suggestions that don't match your brand voice`,
    wordpressFix: `- Review suggestions when editing pages in WordPress — treat them as a writing coach, not a requirement
- Consider running content through a readability tool like **[Hemingway Editor](https://hemingwayapp.com/)** for pages with multiple suggestions`,
    impact: 1,
  },
  near_duplicate_content: {
    description:
      'Pages with very similar content detected via SimHash fingerprinting. Near-duplicates dilute PageRank across multiple URLs and confuse canonical signals, leading search engines to pick the wrong version or reduce rankings for all variants.',
    howToFix: `- **Choose a primary version** of each near-duplicate group and:
  - **Redirect** non-primary versions to the primary with a 301
  - Or add a **canonical tag** pointing to the primary version
- If pages serve **different purposes** (e.g. location variants), differentiate their content significantly (**>30% unique text**)
- Common causes:
  - Print/PDF versions of pages
  - URL parameter variations (\`?sort=\`, \`?ref=\`)
  - HTTP vs HTTPS or www vs non-www variants
  - Boilerplate-heavy pages with little unique content`,
    wordpressFix: `- Check if your theme creates **duplicate archive pages** (category + tag covering the same topic)
- Noindex low-value taxonomy archives in **Yoast → Search Appearance → Taxonomies**
- For location pages with similar content, add **unique local details** (address, team, testimonials, area-specific services)`,
    impact: 3,
  },
  stale_content: {
    description:
      'Pages with a Last-Modified header date older than 12 months. While Last-Modified alone doesn\'t determine freshness, consistently stale content can signal to search engines that the page is unmaintained. For time-sensitive topics, this may negatively affect rankings.',
    howToFix: `- **Review and update** the content on these pages:
  - Refresh statistics, dates, and examples
  - Add new information or developments
  - Remove outdated references
- Even small updates signal freshness to search engines
- For **evergreen content** that doesn't need changes, add a "Last reviewed: [date]" note to show it's been audited
- This is **informational** — not every old page needs updating. Focus on pages targeting competitive or time-sensitive queries`,
    wordpressFix: `Simply **updating and republishing** a post in WordPress updates its modified date. For pages you've reviewed but not changed, make a minor edit (add a space, update a date) and republish. The **[WP Last Modified Info](https://wordpress.org/plugins/wp-last-modified-info/)** plugin can display "Last updated" dates on posts.`,
    impact: 1,
  },
  missing_last_modified: {
    description:
      'Pages that don\'t send a Last-Modified HTTP header. This header helps search engine crawlers determine if content has changed since their last visit, enabling conditional requests (If-Modified-Since) that save server resources and crawl budget.',
    howToFix: `- **Configure your web server** to send \`Last-Modified\` headers for HTML pages
- For **static sites**: ensure your hosting preserves file modification timestamps
- For **dynamic sites**: set the header based on the content's last update time:
  \`\`\`
  Last-Modified: Wed, 05 Mar 2026 10:30:00 GMT
  \`\`\`
- This is **informational** — missing \`Last-Modified\` is common and has minimal direct SEO impact, but it helps crawlers be more efficient`,
    wordpressFix: `WordPress **sends Last-Modified headers by default** for posts. If they're missing:
- A **caching plugin** might be stripping them — check settings in **W3 Total Cache**, **WP Super Cache**, or **WP Rocket**
- Some **CDNs** strip the header — check Cloudflare or your CDN's caching settings
- Add \`Header set Last-Modified\` to \`.htaccess\` if needed`,
    impact: 1,
  },
  large_page_size: {
    description:
      'Pages with HTML response body larger than 500KB. Large pages take longer to download, parse, and render, negatively impacting user experience and Core Web Vitals metrics, especially on mobile devices.',
    howToFix: `- **Reduce page size** by addressing common causes:
  - **Remove inline CSS/JS** — move to external files that can be cached
  - **Reduce DOM size** — simplify deeply nested HTML structures
  - **Paginate long content** — split very long pages into multiple pages
  - **Remove unnecessary HTML** — hidden elements, duplicated markup, empty containers
- Enable **server-side compression** (gzip/brotli) — this reduces transfer size by 60-80%
- Check for **bloated page builders** that generate excessive HTML
- Target: under **200KB of HTML** for optimal performance`,
    wordpressFix: `Common causes in WordPress:
- **Page builders** (Elementor, Divi, WPBakery) generate excessive DOM — simplify layouts or switch to the block editor
- **Too many plugins** injecting CSS/JS into every page — audit and remove unused plugins
- Enable **gzip compression** in your caching plugin (WP Rocket, W3 Total Cache) or \`.htaccess\`
- Use **WP Rocket's "Remove Unused CSS"** feature to eliminate bloat`,
    impact: 2,
  },
  js_rendering_likely: {
    description:
      'These pages have very little visible text content (under 50 words) despite having a large HTML payload (over 10KB). This usually indicates the page relies heavily on JavaScript to render its content, which our static HTML crawler cannot execute. Search engines may also struggle to index this content.',
    howToFix: `- If **JS rendering was enabled** for this crawl, these pages were automatically re-rendered with a headless browser. Check the "JS" badge on the Pages tab to confirm. Pages that remain listed here could not be improved by browser rendering.
- **Implement server-side rendering (SSR)** or **static site generation (SSG)** for key content pages
- At minimum, ensure **critical content is in the initial HTML response** rather than loaded via JavaScript
- Use [Google Search Console's URL Inspection tool](https://search.google.com/search-console) to verify how Google sees these pages
- Consider **pre-rendering services** (Prerender.io, Rendertron) as a quick fix
- For React/Next.js: use \`getServerSideProps\` or \`getStaticProps\`
- For Vue/Nuxt: use \`asyncData\` or \`useFetch\` with SSR mode`,
    wordpressFix: `WordPress itself is server-rendered, so this issue usually indicates a **JavaScript-heavy theme or plugin**:
- Check if a **React/Vue-based theme** is rendering content client-side
- Some **page builders** defer content rendering to JavaScript — check the page source (View Source, not Inspect Element) to see what's in the initial HTML
- If using a **headless WordPress** setup, ensure your frontend has SSR configured`,
    impact: 3,
  },

  // ── Security headers ──────────────────────────────────────────────────
  missing_hsts: {
    description:
      'Pages (200 OK) without a Strict-Transport-Security (HSTS) header. HSTS instructs browsers to always use HTTPS, preventing protocol downgrade attacks and improving security.',
    howToFix: `- **Add the HSTS header** to server responses:
  \`\`\`
  Strict-Transport-Security: max-age=31536000; includeSubDomains
  \`\`\`
- **Start with a short \`max-age\`** (e.g. 300 seconds) for testing, then increase to 31536000 (1 year)
- \`includeSubDomains\` applies HSTS to all subdomains — only add if all subdomains support HTTPS
- Once confident, consider [HSTS preloading](https://hstspreload.org/) for maximum protection`,
    wordpressFix: `Add HSTS via one of these methods:
- **Cloudflare**: Enable HSTS in **SSL/TLS → Edge Certificates → HTTP Strict Transport Security**
- **\`.htaccess\`**: Add \`Header always set Strict-Transport-Security "max-age=31536000; includeSubDomains"\`
- **Security plugins**: **Wordfence** and **iThemes Security** have HSTS toggle options
- **WP Rocket** and some caching plugins can add security headers`,
    impact: 1,
  },
  missing_csp: {
    description:
      'Pages without a Content-Security-Policy header. CSP helps prevent cross-site scripting (XSS) and other code injection attacks by controlling which resources the browser is allowed to load.',
    howToFix: `- **Start with a report-only policy** to audit before enforcing:
  \`\`\`
  Content-Security-Policy-Report-Only: default-src 'self'; script-src 'self'; style-src 'self' 'unsafe-inline'
  \`\`\`
- Review violations in the browser console, then tighten the policy
- **Switch to enforcing mode** once the policy is stable:
  \`\`\`
  Content-Security-Policy: default-src 'self'; script-src 'self' https://cdn.example.com
  \`\`\`
- CSP is **complex to implement** on sites with many third-party scripts (analytics, ads, embeds) — take an incremental approach`,
    wordpressFix: `CSP is **challenging on WordPress** due to inline styles, third-party plugins, and dynamic scripts. Start simple:
- Use the **[HTTP Headers](https://wordpress.org/plugins/http-headers/)** plugin to add CSP headers
- Or add via \`.htaccess\`: \`Header set Content-Security-Policy "default-src 'self' 'unsafe-inline'"\`
- WordPress core and many plugins require \`'unsafe-inline'\` for styles — this weakens CSP but is often necessary`,
    impact: 1,
  },
  missing_x_frame_options: {
    description:
      'Pages without an X-Frame-Options header. This header prevents your pages from being embedded in iframes on other sites, protecting against clickjacking attacks.',
    howToFix: `- **Add the X-Frame-Options header** to server responses:
  \`\`\`
  X-Frame-Options: SAMEORIGIN
  \`\`\`
- Options:
  - \`DENY\` — prevents framing entirely
  - \`SAMEORIGIN\` — allows framing by the same domain (recommended for most sites)
- Alternatively, use the **\`frame-ancestors\` CSP directive** which supersedes X-Frame-Options:
  \`\`\`
  Content-Security-Policy: frame-ancestors 'self'
  \`\`\``,
    wordpressFix: `Add via:
- **\`.htaccess\`**: \`Header always set X-Frame-Options "SAMEORIGIN"\`
- **Cloudflare**: This is set automatically when using Cloudflare
- **Security plugins**: **Wordfence** or **iThemes Security** can set this header
- Note: if you embed your site in iframes (e.g. for previews), use \`SAMEORIGIN\` not \`DENY\``,
    impact: 1,
  },
  missing_x_content_type_options: {
    description:
      'Pages without an X-Content-Type-Options header. This header (typically set to "nosniff") prevents browsers from MIME-sniffing the content type, reducing exposure to drive-by download attacks.',
    howToFix: `- **Add the header** to all server responses:
  \`\`\`
  X-Content-Type-Options: nosniff
  \`\`\`
- This is a **simple, low-risk security header** — there's no reason not to set it
- It prevents browsers from interpreting files as a different MIME type than what's declared in the \`Content-Type\` header`,
    wordpressFix: `Add via:
- **\`.htaccess\`**: \`Header always set X-Content-Type-Options "nosniff"\`
- **Cloudflare**: Automatically sets this header
- **Security plugins**: **Wordfence**, **iThemes Security**, or **Sucuri** include this in their hardening settings
- **[HTTP Headers](https://wordpress.org/plugins/http-headers/)** plugin for a dedicated approach`,
    impact: 1,
  },

  // ── Performance issues ────────────────────────────────────────────────
  slow_response: {
    description:
      'Pages with server response times over 2 seconds. Slow response times negatively impact user experience and can affect search rankings, as page speed is a ranking factor.',
    howToFix: `- **Investigate the slow pages** — common causes:
  - **Slow database queries** — add indexes, optimise queries, implement caching
  - **Unoptimised images** — compress and serve in modern formats
  - **Missing server-side caching** — add page caching, object caching, CDN
  - **Slow third-party scripts** — defer or async-load analytics, ads, widgets
  - **Inadequate server resources** — upgrade hosting if the server is overloaded
- Target: **under 200ms** TTFB (Time to First Byte) for cached pages
- Test with [PageSpeed Insights](https://pagespeed.web.dev/) for detailed recommendations`,
    wordpressFix: `- Install a **caching plugin**: **[WP Rocket](https://wp-rocket.me/)** (paid, best), **[W3 Total Cache](https://wordpress.org/plugins/w3-total-cache/)** (free), or **[LiteSpeed Cache](https://wordpress.org/plugins/litespeed-cache/)** (free, requires LiteSpeed server)
- Enable **object caching** with Redis or Memcached if your host supports it
- Use a **CDN** (Cloudflare free tier works well)
- Install **[Query Monitor](https://wordpress.org/plugins/query-monitor/)** to identify slow database queries and plugins
- Deactivate unnecessary plugins — each plugin adds overhead`,
    impact: 3,
  },
  slow_lcp: {
    description:
      'Pages with Largest Contentful Paint (LCP) over 2.5 seconds. LCP measures how long it takes for the largest visible element (hero image, heading, text block) to render. Google uses LCP as a Core Web Vital ranking signal — pages above 2.5s are rated "Needs Improvement" and above 4s are "Poor".',
    howToFix: `- **Identify the LCP element** — usually a hero image, heading, or large text block
- **For image LCP elements**:
  - Compress and serve in **WebP/AVIF** format
  - Use **responsive \`srcset\`** to serve appropriately sized images
  - **Preload** the LCP image: \`<link rel="preload" as="image" href="hero.webp">\`
  - Add \`fetchpriority="high"\` to the LCP image element for priority resource loading
  - Don't lazy-load the LCP image — use \`loading="eager"\`
- **For text LCP elements**:
  - **Preload critical fonts**: \`<link rel="preload" as="font" href="font.woff2" crossorigin>\`
  - Use \`font-display: swap\` to show text immediately
- **Reduce server response time** (TTFB) — under 800ms target
- **Remove render-blocking CSS/JS** — inline critical CSS, defer non-essential scripts
- [Core Web Vitals documentation](https://web.dev/vitals/)`,
    wordpressFix: `- **WP Rocket**: enable "Remove Unused CSS", "Delay JavaScript", and "Preload" for the LCP image
- **Image optimisation**: use **ShortPixel** or **Imagify** for WebP conversion + compression
- **Preload hero image**: add \`<link rel="preload">\` via \`wp_head\` hook or use **Perfmatters** plugin. Add \`fetchpriority="high"\` to the above-the-fold hero \`<img>\` tag
- Check if a **slider/carousel plugin** is the LCP element — they're notoriously slow. Replace with a static hero image where possible`,
    impact: 4,
  },
  high_cls: {
    description:
      'Pages with Cumulative Layout Shift (CLS) above 0.1. CLS measures visual stability — how much the page layout shifts unexpectedly during loading. High CLS frustrates users and is a Core Web Vital ranking signal. Common causes: images without dimensions, dynamically injected content, web fonts causing layout shifts.',
    howToFix: `- **Add explicit \`width\` and \`height\`** to all images and videos
- **Reserve space for ads and embeds** with CSS \`aspect-ratio\` or fixed-height containers
- **Avoid inserting content above existing content** after page load
- **Fix web font shifts**:
  - Use \`font-display: optional\` (best for CLS) or \`font-display: swap\` with \`size-adjust\`
  - Preload critical fonts
- **Set \`contain: layout\`** on dynamic containers (chat widgets, notification bars)
- **Don't inject banners or cookie notices** that push page content down — use overlays instead
- [CLS optimisation guide on web.dev](https://web.dev/articles/optimize-cls)`,
    wordpressFix: `Common CLS causes in WordPress:
- **Missing image dimensions** — re-upload images through the media library
- **Cookie consent banners** that push content — switch to an **overlay/modal** style
- **Ad injector plugins** that insert ads into content — use fixed-size containers
- **Web fonts** — use the **[OMGF (Optimize My Google Fonts)](https://wordpress.org/plugins/host-webfonts-local/)** plugin to host fonts locally with \`font-display: swap\`
- **Slider plugins** without fixed height — set explicit dimensions`,
    impact: 3,
  },
  poor_performance: {
    description:
      'Pages with a Lighthouse Performance score below 50 (out of 100). The Performance score is a weighted composite of Core Web Vitals and other loading metrics. Scores below 50 indicate significant performance problems that likely affect both user experience and search rankings.',
    howToFix: `- **Run a full Lighthouse audit** at [PageSpeed Insights](https://pagespeed.web.dev/) to identify specific bottlenecks
- Focus on the **heaviest-weighted metrics**: LCP (25%), TBT (30%), CLS (25%)
- Common fixes by priority:
  1. **Optimise images** — compress, convert to WebP/AVIF, use responsive srcset
  2. **Eliminate render-blocking resources** — defer non-critical CSS/JS
  3. **Reduce JavaScript execution time** — code-split, tree-shake, defer third-party scripts
  4. **Enable text compression** — gzip/brotli on the server
  5. **Reduce DOM size** — simplify HTML structure, remove unnecessary wrappers
  6. **Minimise main-thread work** — reduce JS parsing and execution`,
    wordpressFix: `A structured approach for WordPress:
1. Install **[WP Rocket](https://wp-rocket.me/)** or **[LiteSpeed Cache](https://wordpress.org/plugins/litespeed-cache/)** for caching + CSS/JS optimisation
2. Install **[ShortPixel](https://wordpress.org/plugins/shortpixel-image-optimiser/)** or **[Imagify](https://wordpress.org/plugins/imagify/)** for image optimisation
3. Use **[Perfmatters](https://perfmatters.io/)** to disable unused scripts per page (script manager)
4. Audit plugins with **[Query Monitor](https://wordpress.org/plugins/query-monitor/)** — deactivate slow or unnecessary ones
5. Consider switching to a **lightweight theme** (GeneratePress, Astra, Kadence) if using a heavy page builder theme`,
    impact: 3,
  },

  // ── Sitemap issues ────────────────────────────────────────────────────
  missing_sitemap: {
    description:
      'No valid XML sitemap was found for this site. A sitemap helps search engines discover and index your pages more efficiently, especially for large or complex sites.',
    howToFix: `- **Create an XML sitemap** at \`/sitemap.xml\` or \`/sitemap_index.xml\`
- **Submit it to Google Search Console**: Sitemaps → Add a new sitemap → Enter URL
- Reference it in **robots.txt**:
  \`\`\`
  Sitemap: https://example.com/sitemap.xml
  \`\`\`
- Ensure the sitemap:
  - Returns a **200 status code**
  - Contains only **canonical, indexable URLs**
  - Is **under 50MB / 50,000 URLs** per file (use a sitemap index for larger sites)
- [Google's sitemap documentation](https://developers.google.com/search/docs/crawling-indexing/sitemaps/build-sitemap)`,
    wordpressFix: `**Yoast SEO** and **Rank Math** generate XML sitemaps automatically:
- **Yoast**: sitemap at \`/sitemap_index.xml\` — go to **Yoast → General → Features** and ensure "XML sitemaps" is enabled
- **Rank Math**: sitemap at \`/sitemap_index.xml\` — go to **Rank Math → Sitemap Settings**
- Submit the sitemap URL in **[Google Search Console](https://search.google.com/search-console)** → Sitemaps`,
    impact: 3,
  },
  sitemap_url_not_crawled: {
    description:
      'URLs listed in the XML sitemap were not found during the crawl (404, excluded by robots.txt, or unreachable). This means search engines may try to crawl and index non-existent pages.',
    howToFix: `- **Remove deleted or redirected URLs** from your sitemap
- Ensure all sitemap URLs:
  - Return **200 OK** status
  - Are **not blocked by robots.txt**
  - Match the **actual URL format** (check trailing slashes, www vs non-www, http vs https)
- Common causes:
  - URLs from a **previous site structure** still in the sitemap
  - **Trailing slash mismatches** between sitemap URLs and actual URLs
  - URLs **blocked by robots.txt** that are still in the sitemap
- [Google's sitemap troubleshooting](https://developers.google.com/search/docs/crawling-indexing/sitemaps/build-sitemap)`,
    wordpressFix: `If using **Yoast SEO** or **Rank Math**, the sitemap should auto-update when pages are deleted. If stale URLs persist:
- **Clear your SEO plugin's sitemap cache** (Yoast: visit \`/sitemap_index.xml\` to regenerate)
- Check for a **separate sitemap plugin** conflicting with Yoast/Rank Math
- If using a **caching plugin**, purge the sitemap from cache`,
    impact: 3,
  },
  page_not_in_sitemap: {
    description:
      'Crawled pages (200 OK) that are not listed in any XML sitemap. While not strictly required, including important pages in the sitemap helps search engines discover and prioritise them.',
    howToFix: `- **Add important indexable pages** to your sitemap
- Pages that **should be in the sitemap**: service pages, blog posts, product pages, key landing pages
- Pages that **don't need to be in the sitemap**: login, search results, paginated pages, noindexed pages, utility pages
- For dynamic sites, ensure your **sitemap generation** covers all relevant post types and taxonomies
- This is **informational** — not every page needs to be in the sitemap, but important content pages should be`,
    wordpressFix: `In **Yoast SEO → Search Appearance**, check each **Content Type** and **Taxonomy** — ensure "Show in sitemap" is enabled for the types that contain your important content. If specific pages are excluded, check their individual **Yoast Advanced** settings for any overrides.`,
    impact: 2,
  },
  sitemap_url_redirects: {
    description:
      'URLs listed in the XML sitemap return a 3xx redirect response. Sitemaps should only contain URLs that return 200 OK. Including redirecting URLs wastes crawl budget and sends conflicting signals to search engines about which URL is canonical.',
    howToFix: `- **Replace redirecting URLs** in the sitemap with their **final destination URLs**
- If the redirect is permanent (301), update the sitemap entry to the target URL
- **Remove URLs** that redirect to pages outside the sitemap scope
- Common causes:
  - HTTP URLs in sitemap after migrating to HTTPS
  - Old slugs in sitemap after changing permalinks
  - www/non-www mismatches
- [Google's sitemap best practices](https://developers.google.com/search/docs/crawling-indexing/sitemaps/build-sitemap)`,
    wordpressFix: `**Yoast SEO** and **Rank Math** should automatically exclude redirected URLs. If they persist:
- Regenerate the sitemap by visiting \`/sitemap_index.xml\`
- Check for a **separate sitemap plugin** that's out of sync
- Clear your **caching plugin's cache** — the sitemap may be cached with old URLs
- If using the **Redirection** plugin, ensure redirected posts are **trashed** not just redirected (Yoast only excludes trashed/drafts)`,
    impact: 3,
  },
  sitemap_url_noindex: {
    description:
      'URLs listed in the XML sitemap have a noindex directive (via meta robots or X-Robots-Tag). This is contradictory — the sitemap tells search engines to crawl and index the URL, while noindex tells them not to index it. Search engines may eventually drop the URL from their index.',
    howToFix: `- Either **remove the noindex directive** if the page should be indexed
- Or **remove the URL from the sitemap** if it should not be indexed
- Sitemaps should **only contain URLs you want search engines to index**
- This is a **clear contradiction** that confuses search engines — resolve it one way or the other
- [Google's noindex documentation](https://developers.google.com/search/docs/crawling-indexing/robots-meta-tag)`,
    wordpressFix: `**Yoast SEO** should automatically exclude noindexed pages from the sitemap. If they appear:
- Check if the **noindex was added after** the sitemap was cached — purge caches
- Check for conflicting SEO plugins adding noindex while Yoast manages the sitemap
- In **Yoast → Search Appearance**, review Content Types — if a type is set to noindex, it shouldn't appear in the sitemap`,
    impact: 4,
  },
  sitemap_url_wrong_canonical: {
    description:
      'URLs listed in the XML sitemap have a canonical tag pointing to a different URL. This sends mixed signals — the sitemap says "this URL is important" while the canonical says "prefer this other URL instead". Search engines may choose to ignore either signal.',
    howToFix: `- **Replace the sitemap entry** with the canonical URL:
  - If page A canonicals to page B, put **page B** in the sitemap, not page A
- Or **update the canonical tag** to be self-referencing if the page should be indexed at its current URL
- Ensure sitemap URLs and canonical URLs are **consistent** — they should agree on which URL is preferred
- [Google's canonicalization guide](https://developers.google.com/search/docs/crawling-indexing/canonicalization)`,
    wordpressFix: `Check the **Yoast SEO → Advanced** tab on the affected pages to see if a custom canonical is set. If so, either:
- Clear the custom canonical (let it self-reference)
- Or ensure the canonical target is in the sitemap instead`,
    impact: 3,
  },

  // ── Canonical chain issues ────────────────────────────────────────────
  canonical_chain: {
    description:
      'Page A has a canonical tag pointing to page B, but page B has a canonical tag pointing to a different page C. This creates a canonical chain (A→B→C) which dilutes the signal and may cause search engines to pick an unexpected canonical URL or ignore the directive entirely.',
    howToFix: `- **Update canonical tags** so every page in the chain points directly to the **final canonical URL**
- Example fix:
  \`\`\`
  Before: Page A → canonical to B, Page B → canonical to C
  After:  Page A → canonical to C, Page B → canonical to C
  \`\`\`
- Or if B is the intended canonical, make B **self-referencing** and point A to B
- [Google's canonicalization documentation](https://developers.google.com/search/docs/crawling-indexing/canonicalization)`,
    wordpressFix: `Check **Yoast SEO → Advanced** on each page in the chain. Clear any custom canonical URLs that create chains. If the chain is caused by **redirect + canonical** stacking, fix the redirect first (see the Redirection plugin), then ensure the canonical points to the final live URL.`,
    impact: 3,
  },
  canonical_to_noindex: {
    description:
      'A page\'s canonical tag points to another page that has a noindex directive. This is a critical conflict — the canonical says "consider this other page as the original" but that page tells search engines not to index it. The result is that neither page may appear in search results.',
    howToFix: `- This is a **critical issue** — fix immediately:
  - **Remove the noindex** from the canonical target if it should be indexed
  - Or **change the canonical** to point to an indexable page
- This usually indicates misconfigured CMS settings or template-level canonical rules
- Both the source page and canonical target may **disappear from search results** until fixed
- [Google's canonicalization documentation](https://developers.google.com/search/docs/crawling-indexing/canonicalization)`,
    wordpressFix: `Check both pages in the WordPress editor:
- On the **source page**: Yoast Advanced → clear the custom canonical URL
- On the **target page**: Yoast Advanced → ensure "Allow search engines to show this page" is **Yes**
- This often happens when a page type is set to noindex globally in **Yoast → Search Appearance** but individual pages canonical to it`,
    impact: 5,
  },
  canonical_to_redirect: {
    description:
      'A page\'s canonical tag points to a URL that returns a 3xx redirect. Search engines have to follow the redirect to find the real canonical, adding unnecessary complexity. This can dilute link equity and cause crawl budget waste.',
    howToFix: `- **Update the canonical tag** to point directly to the **final destination URL** (the URL after following all redirects)
- Don't chain: \`canonical → redirect → final page\`
- Instead: \`canonical → final page\` directly
- Check if the canonical target's URL changed (slug update, domain migration) and update accordingly
- [Google's canonicalization best practices](https://developers.google.com/search/docs/crawling-indexing/canonicalization)`,
    wordpressFix: `Edit the page in WordPress → **Yoast Advanced** → update the "Canonical URL" field to the final destination URL (not the one that redirects). If you recently changed a page's **permalink/slug**, Yoast may still have the old URL as the canonical — clear it to auto-generate.`,
    impact: 3,
  },
  canonical_to_4xx: {
    description:
      'A page\'s canonical tag points to a URL that returns a 4xx error (typically 404 Not Found). This means the page is telling search engines "my canonical version is this broken page", which causes search engines to distrust the canonical signal and may lead to the page being dropped from the index.',
    howToFix: `- **Fix immediately** — this is a critical issue:
  - Update the canonical tag to point to a **valid, accessible page** (200 OK)
  - Or set a **self-referencing canonical** if this page is the primary version
  - Or **restore/redirect** the 4xx canonical target URL
- This commonly happens when a page is deleted but other pages still canonical to it
- [Google's canonicalization documentation](https://developers.google.com/search/docs/crawling-indexing/canonicalization)`,
    wordpressFix: `Edit the page → **Yoast Advanced** → clear the "Canonical URL" field (this will auto-generate a self-referencing canonical). If the canonical target was a deleted page, either **restore it** from the Trash or set up a **301 redirect** via the Redirection plugin.`,
    impact: 5,
  },

  // ── Readability ───────────────────────────────────────────────────────
  low_readability: {
    description:
      'Pages with a Flesch-Kincaid readability score below 30 (on a 0-100 scale). Difficult-to-read content may struggle to engage users and can indirectly affect SEO through higher bounce rates and lower dwell time.',
    howToFix: `- **Simplify the writing** — aim for a readability score above 60 for general audiences:
  - Use **shorter sentences** (under 20 words)
  - Use **common, everyday words** instead of jargon
  - Break long paragraphs into **2-3 sentence chunks**
  - Use **subheadings** (H2, H3) every 300-400 words
  - Use **bullet points** and numbered lists for complex information
- Note: some content (legal, medical, technical) naturally scores lower — focus on making it as accessible as possible for your audience
- Tools like [Hemingway Editor](https://hemingwayapp.com/) can highlight complex sentences`,
    wordpressFix: `**Yoast SEO** includes a **readability analysis** in the editor — check the "Readability" tab below each post. It highlights:
- Sentences that are too long
- Paragraphs that need breaking up
- Passive voice usage
- Transition word suggestions

Follow Yoast's traffic-light indicators to improve each page's readability score.`,
    impact: 2,
  },

  // ── Structure issues ──────────────────────────────────────────────────
  high_crawl_depth: {
    description:
      'Pages requiring 4 or more clicks from the start URL to reach. Deep pages are harder for search engines to discover and crawl, may receive less PageRank, and indicate poor site architecture.',
    howToFix: `- **Flatten your site structure** so important pages are reachable within **3 clicks** of the homepage
- Strategies:
  - Add **internal links** from higher-level pages to deep content
  - Improve **navigation menus** — add key pages to the main menu or footer
  - Create **hub/pillar pages** that link to related deeper content
  - Add **breadcrumb navigation** to expose the full path
  - Add **"Related posts"** or **"You might also like"** sections
- The goal: every important page should be **3 clicks or fewer** from the homepage`,
    wordpressFix: `- Review your **menu structure** in **Appearance → Menus** — add key deep pages to the main menu
- Use **category archives** as hub pages that link to all posts in that category
- Install a **Related Posts plugin** (e.g. YARPP, Contextual Related Posts) to auto-link between posts
- Add **breadcrumbs** via Yoast SEO (enable in Yoast → Search Appearance → Breadcrumbs)`,
    impact: 3,
  },

  // ── URL structure issues ──────────────────────────────────────────────
  url_too_long: {
    description:
      'Pages with URLs longer than 115 characters. Very long URLs are harder for users to share, may be truncated in search results, and can indicate poor URL structure or excessive nesting.',
    howToFix: `- **Shorten URLs** by:
  - Removing unnecessary **path segments** (e.g. \`/2026/03/07/\` date prefixes)
  - Removing **query parameters** and session IDs
  - Using concise, descriptive slugs: \`/services/web-design/\` not \`/our-services/web-design-services-newcastle-nsw/\`
- Set up **301 redirects** from old long URLs to new short ones
- Keep URLs **under 75 characters** where possible for clean sharing`,
    wordpressFix: `- Change **permalink structure** in **Settings → Permalinks** — use "Post name" (\`/%postname%/\`) instead of date-based or category-based structures
- Edit individual page **slugs** in the block editor (click the URL below the title)
- Remove unnecessary parent categories from URLs: Yoast SEO Premium can strip \`/category/\` from URLs`,
    impact: 2,
  },
  url_has_underscores: {
    description:
      'URLs containing underscores in the path. Google treats underscores as word joiners (not word separators), so "web_design" is treated as one word rather than "web design".',
    howToFix: `- **Replace underscores with hyphens** in URL slugs:
  \`\`\`
  Before: /services/web_design/
  After:  /services/web-design/
  \`\`\`
- Set up **301 redirects** from underscore URLs to hyphenated versions to preserve link equity
- Google treats hyphens as **word separators** but underscores as **joiners** — this directly affects how your URL keywords are parsed`,
    wordpressFix: `- Edit each page's **slug** (URL) in the block editor to replace underscores with hyphens
- Set up 301 redirects using the **[Redirection](https://wordpress.org/plugins/redirection/)** plugin
- WordPress uses hyphens by default — underscores usually come from **manual slug editing** or imported content`,
    impact: 2,
  },
  url_has_uppercase: {
    description:
      'URLs with uppercase characters in the path. URLs are case-sensitive on most servers, meaning /About and /about are different pages. This can cause duplicate content issues.',
    howToFix: `- **Use lowercase for all URL paths** — this is the web standard
- Configure **server-side redirects** to redirect uppercase URLs to lowercase:
  \`\`\`
  /About-Us → 301 → /about-us
  \`\`\`
- Update internal links to use lowercase URLs
- For new content, always use lowercase slugs`,
    wordpressFix: `WordPress generates **lowercase slugs by default**. Uppercase URLs usually come from:
- **Manual slug editing** — edit the page and change the slug to lowercase
- **Imported content** with uppercase slugs — bulk-fix via Better Search Replace
- Add a **rewrite rule** in \`.htaccess\` to force lowercase, or use the **[Redirection](https://wordpress.org/plugins/redirection/)** plugin for individual redirects`,
    impact: 2,
  },
  url_has_parameters: {
    description:
      'URLs containing query parameters (e.g., ?id=123&sort=name). Query parameters can create infinite URL variations, waste crawl budget, and cause duplicate content if not handled properly.',
    howToFix: `- Review whether **query parameters are necessary** for the page's content
- Use **canonical tags** to consolidate parameter variations:
  \`\`\`html
  <!-- On /products?sort=price -->
  <link rel="canonical" href="https://example.com/products/">
  \`\`\`
- Consider using **path-based URLs** instead of parameters: \`/products/category/shoes/\` instead of \`/products?category=shoes\`
- This is **informational** — some parameters are necessary (search queries, filters). Focus on parameters that create duplicate content`,
    wordpressFix: `Common parameter sources in WordPress:
- **UTM tracking parameters** — handled by canonical tags (Yoast adds these automatically)
- **WooCommerce filter/sort parameters** — ensure canonical tags point to the unparameterised URL
- **Preview/draft parameters** (\`?preview=true\`) — these should be noindexed automatically
- If parameters create indexable duplicates, use **Yoast SEO's** canonical tag feature or add URL parameter handling in [Google Search Console](https://search.google.com/search-console)`,
    impact: 1,
  },
  url_double_slashes: {
    description:
      'URLs containing consecutive slashes in the path (e.g., /page//subpage). Double slashes create technically different URLs that may serve duplicate content or return errors.',
    howToFix: `- **Fix the source** of double slashes — common causes:
  - Misconfigured **base URL** or site URL (trailing slash + leading slash)
  - Concatenation bugs in templates: \`baseUrl + "/" + path\` where \`baseUrl\` already ends with \`/\`
- Add a **server-side redirect rule** to normalise double slashes:
  \`\`\`
  /page//subpage → 301 → /page/subpage
  \`\`\`
- Update internal links that contain double slashes`,
    wordpressFix: `Check **Settings → General** — ensure WordPress Address and Site Address end with a single slash or no slash. If double slashes appear in content links, use **[Better Search Replace](https://wordpress.org/plugins/better-search-replace/)** to fix them in the database. Add a redirect rule in \`.htaccess\`:
\`\`\`
RewriteRule ^(.*)//(.*)$ /$1/$2 [R=301,L]
\`\`\``,
    impact: 2,
  },

  // ── Internationalisation ──────────────────────────────────────────────
  hreflang_missing_return: {
    description:
      'Hreflang annotations that lack reciprocal (return) links. When page A declares hreflang pointing to page B, page B must declare hreflang pointing back to page A. Without return links, search engines may ignore the hreflang annotations entirely.',
    howToFix: `- **Ensure all hreflang annotations are bidirectional**:
  \`\`\`html
  <!-- On English page (page A) -->
  <link rel="alternate" hreflang="en" href="https://example.com/page/">
  <link rel="alternate" hreflang="fr" href="https://example.com/fr/page/">

  <!-- On French page (page B) — MUST link back -->
  <link rel="alternate" hreflang="fr" href="https://example.com/fr/page/">
  <link rel="alternate" hreflang="en" href="https://example.com/page/">
  \`\`\`
- **Include a self-referencing hreflang tag** on every page
- [Google's hreflang documentation](https://developers.google.com/search/docs/specialty/international/localized-versions)`,
    wordpressFix: `Use a **multilingual plugin** that handles hreflang automatically:
- **[WPML](https://wpml.org/)** — adds reciprocal hreflang tags between translated pages
- **[Polylang](https://wordpress.org/plugins/polylang/)** — free alternative that also manages hreflang
- Both plugins ensure return links are added when you **connect translated pages** in the editor`,
    impact: 3,
  },
  hreflang_invalid_target: {
    description:
      'Hreflang annotations pointing to URLs that do not return a 200 status code, or were not found during the crawl. Invalid targets waste crawl budget and confuse search engines about your international content structure.',
    howToFix: `- **Update hreflang annotations** to point to valid, accessible URLs (200 OK)
- **Remove hreflang references** to:
  - Deleted pages (404)
  - Redirected pages (301/302) — point to the final destination instead
  - Unreachable or blocked pages
- Verify all language variants are **live and accessible**
- [Google's hreflang troubleshooting](https://developers.google.com/search/docs/specialty/international/localized-versions)`,
    wordpressFix: `If using **WPML** or **Polylang**, check that all **translated versions** of the page are published (not draft or trashed). Disconnect or remove translation links for pages that no longer exist. The plugin should auto-update hreflang tags when translations are published or removed.`,
    impact: 3,
  },
  og_locale_mismatch: {
    description:
      'Pages where the HTML `<html lang>` attribute disagrees with the Open Graph `og:locale` meta tag. Common example: `<html lang="en-AU">` with `<meta property="og:locale" content="en_US">`. Search engines and social platforms read these as contradictory signals about the page\'s intended audience, which can affect how the page is surfaced in locale-specific search results and social previews.',
    howToFix: `- **Match the language prefix** between \`<html lang>\` and \`og:locale\`. The underscore-vs-hyphen format difference is expected (\`en-AU\` vs \`en_AU\`), but the **language prefix and region should agree**.
- **For Australian content**: \`<html lang="en-AU">\` → \`<meta property="og:locale" content="en_AU">\`.
- **For US content**: \`<html lang="en-US">\` → \`<meta property="og:locale" content="en_US">\`.
- **Default-en is rarely right** — if your site is Australian and your OG locale still says \`en_US\`, that's almost certainly a plugin default that was never updated.`,
    wordpressFix: `**Yoast SEO** and **Rank Math** both default \`og:locale\` to \`en_US\` unless you've set the site language. Fix paths:
- **Yoast SEO** → SEO → Settings → Site basics → set **Site language** to match the content region.
- **Rank Math** → General Settings → Social → **Locale** field.
- **WPML/Polylang** users: the multilingual plugin usually overrides og:locale per translated page — check that the plugin is actively setting it and not letting Yoast/Rank Math's default leak through.
- Also confirm WordPress **Site Language** (Settings → General) matches the intended region, as many SEO plugins read from it.`,
    impact: 3,
  },

  // ── Internal authority ────────────────────────────────────────────────
  low_inrank_pages: {
    description:
      'Content pages with substantial text (>200 words) but very low internal authority (InRank <1). These pages receive few or no internal links, making them hard for search engines and users to discover. InRank is calculated using a PageRank-like algorithm across your internal link graph.',
    howToFix: `- **Add internal links to these pages** from relevant, higher-authority pages
- Strategies to boost internal authority:
  - Link from **related blog posts** with contextual anchor text
  - Add to **"Related Services"** or **"You might also like"** sections
  - Include in **navigation menus** or footer links if appropriate
  - Create **hub/pillar pages** that link to these as supporting content
- Pages with good content but low InRank are **prime candidates** for internal linking improvements
- Even 2-3 contextual internal links from authoritative pages can significantly boost a page's InRank`,
    wordpressFix: `- Use **Yoast SEO Premium's internal linking suggestions** to find relevant linking opportunities while editing posts
- Install **[Link Whisper](https://linkwhisper.com/)** for AI-powered internal link suggestions across your entire site
- Add these pages to **category archives** and ensure the categories appear in your navigation
- Use **Related Posts** plugins (YARPP, Contextual Related Posts) to auto-link from similar content`,
    impact: 2,
  },

  // ── Accessibility / Standards ─────────────────────────────────────────
  missing_lang_attribute: {
    description:
      'Pages without a lang attribute on the <html> tag. The lang attribute helps search engines understand the language of the page content and is essential for screen readers and assistive technology.',
    howToFix: `- **Add a \`lang\` attribute** to the \`<html>\` tag:
  \`\`\`html
  <html lang="en-AU">
  \`\`\`
- Use the appropriate [BCP 47 language tag](https://www.iana.org/assignments/language-subtag-registry) for your content:
  - \`en\` — English (generic)
  - \`en-AU\` — Australian English
  - \`en-GB\` — British English
  - \`fr\` — French
- This should be set **once** in the site template and apply to all pages`,
    wordpressFix: `WordPress sets the \`lang\` attribute automatically based on **Settings → General → Site Language**. If it's missing:
- Ensure the **Site Language** is set (not left blank)
- Check your theme's \`header.php\` — it should use \`<?php language_attributes(); ?>\` in the \`<html>\` tag
- If the theme hardcodes \`<html>\` without \`language_attributes()\`, update the template`,
    impact: 3,
  },
  missing_favicon: {
    description:
      'Pages without a favicon (no <link rel="icon"> or <link rel="shortcut icon"> found). Favicons appear in browser tabs, bookmarks, and search results. Missing favicons cause 404 errors in server logs and look unprofessional.',
    howToFix: `- **Add favicon links** to the \`<head>\` section:
  \`\`\`html
  <link rel="icon" href="/favicon.ico" sizes="32x32">
  <link rel="icon" href="/favicon.svg" type="image/svg+xml">
  <link rel="apple-touch-icon" href="/apple-touch-icon.png">
  \`\`\`
- Generate favicons for all platforms using [RealFaviconGenerator](https://realfavicongenerator.net/)
- Place \`favicon.ico\` at the **site root** — browsers look there by default even without a \`<link>\` tag
- Google displays favicons in **search results** — a missing favicon shows a generic globe icon`,
    wordpressFix: `Go to **Appearance → Customise → Site Identity → Site Icon** and upload a square image (at least 512x512px). WordPress will automatically generate all required favicon sizes and add the \`<link>\` tags to every page.`,
    impact: 1,
  },

  // ── Outbound link quality ─────────────────────────────────────────────
  // (spammy_outbound_links is in the Links section above)

  // ── AI Readiness ──────────────────────────────────────────────────────
  ai_fully_blocked: {
    description:
      'This site blocks ALL 12 known AI crawlers (GPTBot, ClaudeBot, PerplexityBot, etc.) via robots.txt. As AI-powered search grows (Perplexity, ChatGPT Search, Google AI Overviews), fully blocking AI crawlers means your content will not appear in any AI search results.',
    howToFix: `- **Decide which AI platforms to allow** based on your content strategy
- At minimum, consider allowing:
  - **Google-Extended** — used for Google AI Overviews (the AI answers in Google Search)
  - **GPTBot** — used for ChatGPT Search
- **Edit \`robots.txt\`** to selectively allow AI crawlers:
  \`\`\`
  # Allow Google AI
  User-agent: Google-Extended
  Allow: /

  # Allow ChatGPT
  User-agent: GPTBot
  Allow: /
  \`\`\`
- If you want to provide content on your terms, create an **[llms.txt](https://llmstxt.org/)** file
- This is a **strategic decision** — blocking AI crawlers isn't inherently wrong, but it has visibility consequences`,
    wordpressFix: `Edit your **robots.txt** — if using Yoast SEO, go to **Yoast → Tools → File editor → robots.txt**. Remove or modify the \`Disallow: /\` rules for AI crawlers you want to allow. If the robots.txt is managed by your hosting provider, edit it via **FTP** or your host's **file manager**.`,
    impact: 3,
  },
  ai_partially_blocked: {
    description:
      'This site blocks some AI crawlers in robots.txt but allows others. This creates an inconsistent AI visibility strategy — your content appears in some AI platforms but not others.',
    howToFix: `- **Review which AI crawlers are blocked** and decide on a consistent policy:
  - **Allow all** — maximum AI search visibility
  - **Block all** — maximum content control (consider adding llms.txt)
  - **Selective** — deliberately choose platforms based on their terms and your strategy
- Common AI crawlers to consider:
  - \`GPTBot\` (OpenAI/ChatGPT), \`ClaudeBot\` (Anthropic), \`PerplexityBot\` (Perplexity)
  - \`Google-Extended\` (Google AI), \`Bytespider\` (ByteDance), \`CCBot\` (Common Crawl)
- Document your AI content policy so future site maintainers understand the reasoning`,
    wordpressFix: `Edit robots.txt via **Yoast → Tools → File editor** or via FTP. Review all \`User-agent\` blocks and ensure your AI crawler rules are consistent with your content strategy.`,
    impact: 2,
  },
  ai_blocked_no_llms_txt: {
    description:
      'This site blocks AI crawlers via robots.txt but does not provide an llms.txt file. Without llms.txt, blocked AI systems have no alternative way to understand your content, reducing your AI search visibility to zero.',
    howToFix: `- **Option A**: Allow AI crawlers in robots.txt (remove \`Disallow: /\` rules for AI user-agents)
- **Option B**: Create an \`llms.txt\` file at the site root that provides a structured summary:
  \`\`\`
  # Example llms.txt
  > Site name and description

  ## Key Pages
  - [Services](https://example.com/services/): Description of services
  - [About](https://example.com/about/): Company background

  ## Content Guidelines
  This content may be used for AI responses with attribution.
  \`\`\`
- See the [llms.txt specification](https://llmstxt.org/) for the full format
- The best approach is usually **both**: allow crawlers AND provide llms.txt for structured context`,
    wordpressFix: `Create an \`llms.txt\` file and upload it to your WordPress root directory (same level as \`wp-config.php\`) via **FTP** or your host's **file manager**. There are also WordPress plugins emerging for llms.txt generation — search the plugin directory for "llms.txt".`,
    impact: 2,
  },
  ai_contradictory_signals: {
    description:
      'This site has an llms.txt file but also blocks AI crawlers in robots.txt. These are contradictory signals — llms.txt invites AI systems to read your content, while robots.txt blocks them from crawling it.',
    howToFix: `- **Align your AI content strategy** — pick one approach:
  - **If you want AI visibility**: remove the \`Disallow: /\` rules for AI crawlers from robots.txt AND keep llms.txt
  - **If you want to block AI**: remove the llms.txt file AND keep the robots.txt blocks
- Having both sends **mixed signals** that different AI systems may interpret differently
- The robots.txt blocks will generally win (crawlers check robots.txt first), making the llms.txt ineffective`,
    wordpressFix: `Edit robots.txt via **Yoast → Tools → File editor** to remove AI crawler blocks, OR delete the \`llms.txt\` file from your WordPress root directory. Choose one consistent approach.`,
    impact: 3,
  },
  ai_missing_llms_txt: {
    description:
      'This site does not have an llms.txt file. The llms.txt standard (llmstxt.org) provides a structured summary of your site\'s content specifically for AI systems, improving how they understand and represent your content.',
    howToFix: `- **Create an \`llms.txt\` file** at the root of your site (e.g. \`example.com/llms.txt\`)
- Include:
  - Site name and description
  - Key pages with brief descriptions
  - Content guidelines for AI usage
- See the [llms.txt specification](https://llmstxt.org/) for the format and examples
- This is **informational** — llms.txt is a new standard and not all sites need it. It's most valuable for content-rich sites that want to control how AI represents their content`,
    wordpressFix: `Create an \`llms.txt\` file and upload it to your WordPress root directory (same level as \`wp-config.php\`) via **FTP** or your host's file manager. Search the WordPress plugin directory for "llms.txt" plugins that can auto-generate the file from your content.`,
    impact: 1,
  },
  ai_noai_meta: {
    description:
      'These pages include "noai", "noimageai", or "notranslate" directives in their meta robots tag. This tells AI systems not to use this content for training or generation purposes. This is a deliberate content control mechanism, not necessarily a problem.',
    howToFix: `- If these directives are **intentional** — no action needed. This is a valid content control mechanism.
- If you **want your content available to AI systems**, remove the directives from the meta robots tag:
  \`\`\`html
  <!-- Remove noai/noimageai from this: -->
  <meta name="robots" content="index, follow, noai, noimageai">
  <!-- Change to: -->
  <meta name="robots" content="index, follow">
  \`\`\`
- Note: AI crawler compliance with \`noai\`/\`noimageai\` directives **varies by platform** — not all respect them
- [Google's robots meta tag documentation](https://developers.google.com/search/docs/crawling-indexing/robots-meta-tag)`,
    wordpressFix: `Check if a **plugin** is adding \`noai\` directives. Some security or privacy plugins add these by default. Check **Yoast → Search Appearance** and your **theme's header.php** for hardcoded meta robots tags. Remove the \`noai\` values if you want AI systems to use your content.`,
    impact: 1,
  },

  // ── Accessibility ─────────────────────────────────────────────────────
  missing_form_labels: {
    description:
      'Pages with form inputs (text fields, selects, textareas) that lack accessible labels. Inputs without labels are inaccessible to screen reader users and can also impact usability for sighted users. Missing labels can result in WCAG 2.1 Level A failures (Success Criterion 1.3.1).',
    howToFix: `- **Add a visible \`<label>\` element** with a matching \`for\` attribute for each input:
  \`\`\`html
  <label for="email">Email address</label>
  <input type="email" id="email" name="email">
  \`\`\`
- For inputs without visible labels, add **\`aria-label\`** or **\`aria-labelledby\`**:
  \`\`\`html
  <input type="search" aria-label="Search the site">
  \`\`\`
- **Placeholder text alone is NOT sufficient** as a label — it disappears on focus and is not reliably announced by screen readers
- Every \`<input>\`, \`<select>\`, and \`<textarea>\` should have an associated label`,
    wordpressFix: `- Check **Contact Form 7** or **WPForms** field settings — ensure labels are enabled (not hidden)
- For **search forms** in widgets, check the theme's \`searchform.php\` template — add \`aria-label\` to the search input
- Page builders (Elementor, Divi) have **label toggle options** in form widgets — enable them
- The **WordPress block editor's** form blocks include labels by default`,
    impact: 2,
  },
  generic_link_text: {
    description:
      'Pages with links using generic anchor text like "click here", "read more", or "learn more". Generic link text provides no context about the destination when read out of context (as in a screen reader\'s link list) and misses an opportunity to include descriptive, keyword-rich anchor text that benefits SEO.',
    howToFix: `- **Replace generic link text** with descriptive anchor text:
  \`\`\`
  Before: "Click here to view our services"
  After:  "View our web design services"

  Before: "Read more"
  After:  "Read more about plantation shutters" (or use the post title)
  \`\`\`
- For **"Read more" links on blog cards**, include visually hidden context:
  \`\`\`html
  <a href="/post">Read more<span class="sr-only"> about plantation shutters</span></a>
  \`\`\`
- Screen reader users often navigate by **listing all links** — generic text like "click here" repeated 10 times is useless in that context`,
    wordpressFix: `- Edit **"Read more"** text in your theme: many themes let you customise the excerpt link text in **Appearance → Customise** or theme settings
- In the **block editor**, select the link and change the anchor text to something descriptive
- For **WooCommerce** "Read more" buttons, customise via the \`woocommerce_product_add_to_cart_text\` filter or theme settings
- **Yoast SEO** flags generic link text in its content analysis — follow its suggestions`,
    impact: 2,
  },
  empty_buttons: {
    description:
      'Pages with <button> elements that have no text content and no aria-label attribute. Screen reader users cannot determine the purpose of these buttons. This is a WCAG 2.1 Level A failure (Success Criterion 4.1.2) and often indicates icon-only buttons missing accessible names.',
    howToFix: `- **Add descriptive text** inside the button, or add an \`aria-label\`:
  \`\`\`html
  <!-- Option 1: Visible text -->
  <button>Close</button>

  <!-- Option 2: Icon + visually hidden text -->
  <button><svg>...</svg><span class="sr-only">Close dialog</span></button>

  <!-- Option 3: aria-label -->
  <button aria-label="Close dialog"><svg>...</svg></button>
  \`\`\`
- Common icon-only buttons that need labels: **close/dismiss**, **hamburger menu**, **search**, **social media links**, **carousel arrows**
- The \`.sr-only\` CSS class makes text visible to screen readers but hidden visually`,
    wordpressFix: `- Check your **theme's navigation toggle** (hamburger menu) — add \`aria-label="Open menu"\` in \`header.php\`
- **Slider/carousel plugins** often have unlabelled prev/next buttons — check plugin settings for accessibility options
- **Modal/popup plugins** often have unlabelled close buttons — add \`aria-label="Close"\`
- Many modern themes include these labels by default — ensure you're running the **latest theme version**`,
    impact: 2,
  },
  missing_skip_nav: {
    description:
      'Pages without a "skip to content" link at the top of the page. Skip navigation links allow keyboard and screen reader users to bypass repetitive navigation menus and jump directly to the main content. This is a WCAG 2.1 Level A requirement (Success Criterion 2.4.1).',
    howToFix: `- **Add a skip link** as the first focusable element on the page:
  \`\`\`html
  <body>
    <a href="#main-content" class="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:bg-white focus:p-2 focus:z-50">
      Skip to content
    </a>
    <header>...</header>
    <main id="main-content">...</main>
  </body>
  \`\`\`
- The link should be **visually hidden** by default but **visible on keyboard focus** (Tab key)
- The target (\`#main-content\`) should be on the \`<main>\` element or the start of the primary content area
- Test by pressing **Tab** on page load — the skip link should appear as the first focused element`,
    wordpressFix: `Most modern WordPress themes include skip navigation links. If yours doesn't:
- Check your theme's \`header.php\` — add the skip link right after \`<body>\`
- Popular themes that include it: **Astra**, **GeneratePress**, **Kadence**, **Twenty Twenty-Four**
- If using a **child theme**, add the skip link in the child theme's \`header.php\`
- The **[WP Accessibility](https://wordpress.org/plugins/wp-accessibility/)** plugin can add skip links without editing theme files`,
    impact: 1,
  },
  missing_main_landmark: {
    description:
      'Pages without a <main> element or an element with role="main". The main landmark identifies the primary content area of the page, helping screen reader users navigate directly to the content. This is a WCAG 2.1 Level A best practice.',
    howToFix: `- **Wrap the primary content** of each page in a \`<main>\` element:
  \`\`\`html
  <header>...</header>
  <main>
    <!-- Primary page content here -->
  </main>
  <footer>...</footer>
  \`\`\`
- There should be **exactly one \`<main>\` element** per page
- The \`<main>\` element should contain **only the unique content** (not headers, footers, or navigation)
- If you cannot use the semantic \`<main>\` tag, add \`role="main"\` to the content wrapper:
  \`\`\`html
  <div id="content" role="main">...</div>
  \`\`\``,
    wordpressFix: `Check your theme's **page template** (\`page.php\`, \`single.php\`, \`index.php\`) — the content area should be wrapped in \`<main>\`. Most modern themes do this, but older themes use \`<div id="content">\` without a \`<main>\` wrapper. Options:
- **Update your theme** to the latest version (most have added \`<main>\` in recent updates)
- Edit the theme template to change \`<div id="content">\` to \`<main id="content">\`
- The **[WP Accessibility](https://wordpress.org/plugins/wp-accessibility/)** plugin can add landmarks without editing theme files`,
    impact: 1,
  },

  // ── Core Web Vitals ───────────────────────────────────────────────────
  // (slow_lcp, high_cls, poor_performance are in the Performance section above)

  // ── Hidden Links (Cloaking Detection) ──────────────────────────────────

  hidden_links: {
    description: `This page contains **links that are hidden from users but visible to search engines**. Hidden links are a strong indicator of SEO spam, cloaking, or a compromised site. Search engines like Google consider hidden links a violation of their webmaster guidelines and may penalise the site.

Hidden links are detected by comparing the total number of links on a page with the number of links visible to users. The difference represents links concealed via CSS (\`display: none\`, \`visibility: hidden\`, off-screen positioning) or other techniques.`,
    howToFix: `1. **Inspect the page source** — search for links with suspicious CSS:
   - \`display: none\` or \`visibility: hidden\` on the link or a parent element
   - Absolute positioning with large negative values (\`left: -9999px\`, \`top: -9999px\`)
   - \`font-size: 0\`, \`color\` matching background, or \`opacity: 0\`
   - \`overflow: hidden\` on a zero-height container
2. **Check for injected content** — if you did not add these links, your site may be compromised:
   - Scan for malware or unauthorised code injections
   - Check recently modified theme/plugin files
   - Review user accounts for unknown administrators
3. **Remove the hidden links** — delete any links that are intentionally hidden from users
4. **Legitimate exceptions** — some hidden links are acceptable:
   - Skip navigation links (\`<a href="#main" class="sr-only">\`) for accessibility
   - Mobile menu links that are only visible when the menu is open
   - These should use \`aria-\` attributes and proper focus management`,
    impact: 3,
  },

  // ── Phase 4: Crawler & Detection Enhancements ─────────────────────────
  title_matches_h1: {
    description:
      'The page title tag and H1 heading contain identical text. While not a direct ranking penalty, using the same text for both wastes an opportunity to target additional keywords and provide more context to search engines.',
    howToFix: `- **Differentiate your title and H1** — use the title for search engines (include brand, keywords) and the H1 for users (clear, engaging heading)
- **Title format**: \`Primary Keyword - Secondary Keyword | Brand\`
- **H1 format**: A clear, descriptive heading that matches the page content
- For example, title: \`Plantation Shutters Newcastle | The Shutter Guy\`, H1: \`Custom Plantation Shutters for Your Home\`
- Keep some keyword overlap for relevance, but don't duplicate word-for-word`,
    wordpressFix: `In **Yoast SEO** or **Rank Math**, customise the SEO title separately from the page title (H1). The page editor title becomes the H1; the SEO title field in the plugin panel is what appears in search results.`,
    impact: 2,
  },
  missing_video_schema: {
    description:
      'Pages with video content (\`<video>\` elements) that lack VideoObject structured data. Adding VideoObject schema helps search engines understand your video content and can enable rich video snippets in search results, including thumbnails, duration, and descriptions.',
    howToFix: `- **Add VideoObject JSON-LD** to pages with video content:
\`\`\`html
<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "VideoObject",
  "name": "Video Title",
  "description": "Brief description of the video",
  "thumbnailUrl": "https://example.com/thumbnail.jpg",
  "uploadDate": "2024-01-15",
  "duration": "PT5M30S",
  "contentUrl": "https://example.com/video.mp4"
}
</script>
\`\`\`
- Include at minimum: \`name\`, \`description\`, \`thumbnailUrl\`, and \`uploadDate\`
- See [Google's Video structured data guide](https://developers.google.com/search/docs/appearance/structured-data/video)`,
    wordpressFix: `Use **Yoast Video SEO** (premium add-on) or **Rank Math PRO** to automatically generate VideoObject schema for embedded videos.`,
    impact: 3,
  },
  excessive_iframes: {
    description:
      'Pages with more than 3 iframe elements. Excessive iframes increase page load time, consume additional memory, and can degrade user experience. Each iframe loads a separate document with its own resources, which compounds the performance impact.',
    howToFix: `- **Audit each iframe** — determine if it is essential for the page
- **Lazy-load iframes** below the fold using \`loading="lazy"\`
- **Replace iframes with lightweight alternatives** where possible:
  - YouTube embeds: use a facade/thumbnail that loads the player on click
  - Maps: use a static image with a link to the interactive map
  - Social feeds: use static content or API-driven rendering
- **Combine functionality** — if multiple iframes serve similar purposes, consolidate them`,
    impact: 2,
  },
  excessive_resources: {
    description:
      'Pages loading more than 15 external JavaScript and CSS files. Each resource requires a separate HTTP request, and many scripts block rendering until they are downloaded and executed. This significantly impacts page load speed and Core Web Vitals.',
    howToFix: `- **Audit your scripts and stylesheets** — remove any that are unused or redundant
- **Bundle and minify** — combine multiple JS/CSS files into fewer bundles
- **Defer non-critical JavaScript** using \`defer\` or \`async\` attributes
- **Lazy-load third-party scripts** (analytics, chat widgets, social embeds) that are not needed for initial render
- **Use \`<link rel="preload">\`** for critical resources
- **Remove unused plugins** that inject their own scripts on every page`,
    wordpressFix: `Use a performance plugin like **WP Rocket**, **Autoptimise**, or **LiteSpeed Cache** to combine and minify CSS/JS files. Deactivate plugins you are not using — many inject scripts site-wide even when not needed on every page.`,
    impact: 3,
  },
  missing_responsive_images: {
    description:
      'Pages with images that lack responsive markup (\`srcset\` attribute or \`<picture>\` element). Without responsive images, mobile devices download full-size desktop images, wasting bandwidth and slowing page load. This negatively impacts Core Web Vitals (especially LCP) and mobile user experience.',
    howToFix: `- **Add \`srcset\` and \`sizes\` attributes** to \`<img>\` tags:
\`\`\`html
<img
  src="image-800.jpg"
  srcset="image-400.jpg 400w, image-800.jpg 800w, image-1200.jpg 1200w"
  sizes="(max-width: 600px) 400px, (max-width: 900px) 800px, 1200px"
  alt="Description"
/>
\`\`\`
- **Use the \`<picture>\` element** for art direction (different crops per breakpoint) or format switching (WebP/AVIF with fallback)
- **Generate multiple image sizes** during your build process or via a CDN with on-the-fly resizing
- Prioritise images above the fold and hero images`,
    wordpressFix: `WordPress **automatically generates multiple image sizes** and adds \`srcset\` when you use the media library. If images are missing \`srcset\`, check if a plugin or theme is stripping it. Ensure images are uploaded through the media library, not hardcoded via HTML.`,
    impact: 3,
  },
}
